import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { MobilePage, MobilePageHeader, mobile } from '../components/mobile'
import { PlusIcon, TrashIcon, FileTextIcon, UserIcon, BuildingIcon, MailIcon } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { logActivity, ActivityTypes, logActivityOnLinkedParents } from '../utils/activityLogger'
import { NotesAndActivityPanel } from '../components/NotesAndActivityPanel'
import { LeadCustomerSearchSelect } from '../components/LeadCustomerSearchSelect'
import { PropertyProjectSearchSelect } from '../components/PropertyProjectSearchSelect'
import { selectionFromIds } from '../lib/leadCustomerSearch'
import { hasLinkedOwner, resolveOwnerContact } from '../lib/resolveOwnerContact'
import { ProposalRequestTabPanel } from '../components/ProposalRequestTabPanel'
import {
  resolveProposalRequestFromProposal,
  type ProposalRequestDisplayData,
} from '../lib/proposalRequestDisplay'
import { SERVICE_TYPE_OPTIONS, buildAutoProposalTitle, isDsnyServiceType } from '../lib/serviceTypeOptions'
import { AddressAutocomplete } from '../components/AddressAutocomplete'
import { fetchServiceItemsGrouped, ServiceItemGrouped } from '../utils/serviceItems'
import { ServiceItemSelect } from '../components/service/ServiceItemSelect'
import { convertLeadToCustomer } from '../lib/leadConversion'
import ConsistencyWarnings from '../components/agreement/ConsistencyWarnings'
import {
  checkConsistency,
  splitIssues,
  getDefaultTemplateTypeForServiceType,
  getDefaultContractTypeForServiceType,
} from '../utils/consistencyRules'
import { AgreementState, IssueTarget, DocumentData, BillingFrequency, BillingMilestone, PricingMode, ContractType } from '../types/agreement'
import BillingFrequencySelect from '../components/agreement/BillingFrequencySelect'
import BillingMethodSelect from '../components/agreement/BillingMethodSelect'
import { BillingDayOfMonthSelect } from '../components/BillingDayOfMonthSelect'
import MilestoneScheduleEditor from '../components/agreement/MilestoneScheduleEditor'
import ProposalValidityFields from '../components/agreement/ProposalValidityFields'
import { LineItemUnitTypeSelect } from '../components/agreement/LineItemUnitTypeSelect'
import { PricingPresentationFields } from '../components/agreement/PricingPresentationFields'
import { buildRateCardLineItems, hasRateCardSeed } from '../lib/rateCardLineItems'
import { todayDateInputValue } from '../lib/dateUtils'
import {
  defaultBillingFrequencyForSave,
  normalizeBillingFrequency,
  normalizeBillingMethod,
} from '../lib/billingFrequencyHelpers'
import {
  createEmptyMilestone,
  fetchProposalMilestones,
  saveProposalMilestones,
} from '../lib/milestoneBilling'
import { buildDefaultDocumentData } from '../lib/proposalDocumentTemplates'
import DocumentDataEditor from '../components/agreement/DocumentDataEditor'
import { resolveProposalSiteAddress } from '../lib/propertyProjectSearch'
import { getDSNYFrequencies } from '../utils/dsnyHelpers'
import { findDsnyPackageItem, type DsnyPackageKind } from '../lib/contractServiceLineItems'

interface ProposalNote {
  timestamp: string
  note: string
}

interface Customer {
  id: string
  company_name?: string
  contact_first_name: string
  contact_last_name: string
}

interface Property {
  id: string
  name: string
  address: string
  customer_id: string
  property_type?: string
}

export default function ProposalEdit() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')
  const [loading, setLoading] = useState(false)
  const [loadingData, setLoadingData] = useState(true)

  const [activeTab, setActiveTab] = useState<'overview' | 'items' | 'billing' | 'notes' | 'request'>('overview')
  const [isTitleManual, setIsTitleManual] = useState(false)
  const [dsnyFreqNotice, setDsnyFreqNotice] = useState<string>('')
  const [showDsnyModal, setShowDsnyModal] = useState(false)
  const [dsnyModalLoading, setDsnyModalLoading] = useState(false)
  const [dsnyModalError, setDsnyModalError] = useState('')
  const [dsnyModalDetected, setDsnyModalDetected] = useState<{
    garbage: number
    recycling: number
    organics: number
    total: number
  } | null>(null)
  const [dsnyVisitsPerWeek, setDsnyVisitsPerWeek] = useState(0)
  const [hallwayVisitsPerWeek, setHallwayVisitsPerWeek] = useState(1)
  const [dsnyPackageKind, setDsnyPackageKind] = useState<DsnyPackageKind>('building_upkeep')

  const [customers, setCustomers] = useState<Customer[]>([])
  const [leads, setLeads] = useState<any[]>([])
  const [selectedLead, setSelectedLead] = useState<any>(null)
  const [selectedCustomerProperties, setSelectedCustomerProperties] = useState<Property[]>([])
  const [entityType, setEntityType] = useState<'customer' | 'lead'>('customer')

  const [proposalData, setProposalData] = useState<{
    title: string
    description: string
    template_type: string
    service_type: string
    contract_type: '' | ContractType
    customer_id: string
    lead_id: string | null
    property_id: string
    property_type: string
    company_name: string
    contact_name: string
    contact_email: string
    contact_phone: string
    total_amount: number
    subtotal: number
    tax_rate: number
    tax_amount: number
    sales_tax_status: string
    payment_terms: number
    valid_until: string
    terms_and_conditions: string
    status: 'draft' | 'sent' | 'viewed' | 'approved' | 'rejected' | 'expired' | 'converted'
    // New fields for contract alignment
    start_date: string
    end_date: string
    billing_frequency: '' | BillingFrequency
    billing_day_of_month: number
    billing_method?: '' | 'per_period' | 'divide_total' | 'actual_usage' | 'progress'
    monthly_amount: number
    late_fee_percentage: number
    customer_notes: string
    dsny_integration: boolean
    dsny_intent_message: string
    is_locked: boolean
    prices_locked: boolean
    proposal_request_id?: string | null
    proposal_request_data?: any
    // Request fields (for unified table)
    record_type?: 'request' | 'proposal'
    request_number?: string | null
    project_type?: string | null
    scope_of_work?: string
    document_data?: DocumentData
  }>({
    title: '',
    description: '',
    template_type: '',
    service_type: '',
    contract_type: 'one_time',
    customer_id: '',
    lead_id: null,
    property_id: '',
    property_type: '',
    company_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    total_amount: 0,
    subtotal: 0,
    tax_rate: 0,
    tax_amount: 0,
    sales_tax_status: '',
    payment_terms: 30,
    valid_until: '',
    terms_and_conditions: '',
    status: 'draft',
    // New fields
    start_date: '',
    end_date: '',
    billing_frequency: 'monthly' as BillingFrequency,
    billing_day_of_month: 1,
    billing_method: '' as '' | 'per_period' | 'divide_total' | 'actual_usage' | 'progress',
    monthly_amount: 0,
    late_fee_percentage: 0,
    customer_notes: '',
    dsny_integration: false,
    dsny_intent_message: '',
    scope_of_work: '',
    document_data: {} as DocumentData,
    is_locked: false,
    prices_locked: false,
  })

  const [proposalCreatedAt, setProposalCreatedAt] = useState<string>('')
  const [billingMilestones, setBillingMilestones] = useState<BillingMilestone[]>([])

  const ownerContactReadOnly = hasLinkedOwner(proposalData.customer_id, proposalData.lead_id)

  const [notes, setNotes] = useState<ProposalNote[]>([])
  const [newNote, setNewNote] = useState('')
  const [savingNote, setSavingNote] = useState(false)
  const [notification, setNotification] = useState<{
    message: string
    type: 'success' | 'error'
  } | null>(null)
  const [showConvertModal, setShowConvertModal] = useState(false)
  const [converting, setConverting] = useState(false)
  const [originalStatus, setOriginalStatus] = useState<string>('')
  const [pendingAction, setPendingAction] = useState<'create-contract' | 'create-job' | null>(null)
  const [showCreateJobModal, setShowCreateJobModal] = useState(false)
  const [showCreatePropertyModal, setShowCreatePropertyModal] = useState(false)
  const [creatingProperty, setCreatingProperty] = useState(false)
  const [newPropertyData, setNewPropertyData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    property_type: '',
  })
  const [proposalRequest, setProposalRequest] = useState<ProposalRequestDisplayData | null>(null)
  const [proposalRecord, setProposalRecord] = useState<Record<string, any> | null>(null)
  const [loadingRequest, setLoadingRequest] = useState(false)
  const [conversionData, setConversionData] = useState({
    company_name: '',
    company_phone: '',
    company_email: '',
    company_website: '',
    company_address: '',
    company_address_line_2: '',
    company_city: '',
    company_state: '',
    company_zip: '',
    contact_name: '',
    email: '',
    phone: '',
    cell: '',
    contacts: [] as Array<{
      name: string
      phone: string
      cell: string
      email: string
    }>,
    properties: [] as Array<{
      name: string
      address: string
      address_line_2: string
      city: string
      state: string
      zip: string
      type: string
      unit_count: string
      work_type: string
      notes: string
    }>,
  })

  interface ProposalLineItem {
    id: string
    service_item_id?: string
    description: string
    quantity: number
    is_variable: boolean // Checkbox for variable items (per hour, per unit, etc.)
    unit_type: string
    unit_price: number
    total_price: number
    sort_order: number
    // Interval/recurrence fields for scheduling
    recurrence_type: 'one_time' | 'daily' | 'weekly' | 'bi_weekly' | 'monthly' | 'quarterly' | 'annually' | 'custom'
    recurrence_interval: number
    interval_unit: 'days_per_week' | 'days_per_month' | 'every_x_weeks' | 'every_x_months' | ''
    service_type?: string
    conversion_status?: string
  }

  const [lineItems, setLineItems] = useState<ProposalLineItem[]>([])
  const [serviceItemsGrouped, setServiceItemsGrouped] = useState<ServiceItemGrouped[]>([])
  const [serviceCategories, setServiceCategories] = useState<any[]>([])

  useEffect(() => {
    if (id) {
      fetchProposalData()
    }
    fetchCustomers()
    fetchLeads()
    fetchServiceItems()
    fetchServiceCategories()
  }, [id])

  useEffect(() => {
    if (proposalRecord) {
      fetchProposalRequest()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalRecord])

  const proposalRequestContact = useMemo(() => {
    if (!proposalRequest) return null
    const customer = customers.find((c) => c.id === proposalData.customer_id)
    const property = selectedCustomerProperties.find((p) => p.id === proposalData.property_id)
    let leadProject: any = null
    if (selectedLead?.projects && proposalData.property_id) {
      if (selectedLead.projects.some((p: any) => p.id === proposalData.property_id)) {
        leadProject = selectedLead.projects.find((p: any) => p.id === proposalData.property_id)
      } else if (proposalData.property_id.includes('project-')) {
        const projectIndex = parseInt(proposalData.property_id.replace('project-', ''), 10)
        if (projectIndex >= 0 && selectedLead.projects[projectIndex]) {
          leadProject = selectedLead.projects[projectIndex]
        }
      }
    }
    return resolveOwnerContact({
      customer,
      lead: selectedLead,
      property,
      leadProject,
    })
  }, [
    proposalRequest,
    customers,
    proposalData.customer_id,
    proposalData.property_id,
    selectedCustomerProperties,
    selectedLead,
  ])

  const fetchServiceItems = async () => {
    const items = await fetchServiceItemsGrouped()
    setServiceItemsGrouped(items)
  }

  const fetchServiceCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('service_categories')
        .select('id, name')
        .order('name')

      if (error) throw error
      setServiceCategories(data || [])
    } catch (error) {
      console.error('Error fetching service categories:', error)
    }
  }

  const getServiceCategoryName = (categoryId: string) => {
    const category = serviceCategories.find((c) => c.id === categoryId)
    return category ? category.name : categoryId
  }

  const fetchLeads = async () => {
    try {
      const { data, error } = await supabase
        .from('leads')
        .select('id, company_name, contact_first_name, contact_last_name')
        .in('stage', ['new', 'contacted', 'qualified', 'proposal_sent']) // Only show open leads (not won or lost)
        .order('company_name')
        .order('contact_first_name')

      if (error) throw error
      setLeads(data || [])
    } catch (error) {
      console.error('Error fetching leads:', error)
    }
  }

  useEffect(() => {
    if (entityType === 'customer' && proposalData.customer_id) {
      fetchPropertiesForCustomer(proposalData.customer_id)
    } else {
      setSelectedCustomerProperties([])
    }
  }, [proposalData.customer_id, entityType])

  // Fetch lead and its projects when lead is selected
  useEffect(() => {
    if (entityType === 'lead' && proposalData.lead_id) {
      fetchLeadWithProjects(proposalData.lead_id)
    } else {
      setSelectedLead(null)
    }
  }, [proposalData.lead_id, entityType])

  // Auto-generate proposal title from address/project and service type
  useEffect(() => {
    let address = ''

    if (entityType === 'customer' && proposalData.property_id) {
      const selectedProperty = selectedCustomerProperties.find(
        (p) => p.id === proposalData.property_id
      )
      if (selectedProperty?.address) {
        address = selectedProperty.address
      }
    } else if (entityType === 'lead' && proposalData.property_id && selectedLead?.projects) {
      let selectedProject: any = null

      if (selectedLead.projects.some((p: any) => p.id === proposalData.property_id)) {
        selectedProject = selectedLead.projects.find((p: any) => p.id === proposalData.property_id)
      } else if (proposalData.property_id.includes('project-')) {
        const projectIndex = parseInt(proposalData.property_id.replace('project-', ''))
        if (projectIndex >= 0 && selectedLead.projects[projectIndex]) {
          selectedProject = selectedLead.projects[projectIndex]
        }
      }

      if (selectedProject?.address) {
        address = selectedProject.address
      }
    }

    if (!isTitleManual && proposalData.service_type) {
      const autoTitle = buildAutoProposalTitle(address, proposalData.service_type)
      if (autoTitle) {
        setProposalData((prev) => ({
          ...prev,
          title: autoTitle,
        }))
      }
    }
  }, [
    proposalData.property_id,
    proposalData.service_type,
    isTitleManual,
    entityType,
    selectedCustomerProperties,
    selectedLead,
  ])

  // Pre-populate company name, contact info when customer/lead is selected
  useEffect(() => {
    if (entityType === 'customer' && proposalData.customer_id) {
      const selectedCustomer = customers.find((c) => c.id === proposalData.customer_id)
      if (selectedCustomer) {
        setProposalData((prev) => ({
          ...prev,
          company_name: selectedCustomer.company_name || '',
          contact_name: `${selectedCustomer.contact_first_name || ''} ${selectedCustomer.contact_last_name || ''}`.trim(),
        }))
        fetchCustomerContactInfo(proposalData.customer_id)
      }
    } else if (entityType === 'lead' && proposalData.lead_id && selectedLead) {
      const primaryContact = selectedLead.contacts && selectedLead.contacts[0] ? selectedLead.contacts[0] : null

      // Handle contact name
      let contactName = ''
      if (primaryContact) {
        if (primaryContact.first_name || primaryContact.last_name) {
          contactName = `${primaryContact.first_name || ''} ${primaryContact.last_name || ''}`.trim()
        } else if (primaryContact.name) {
          contactName = primaryContact.name
        }
      }
      if (!contactName && (selectedLead.contact_first_name || selectedLead.contact_last_name)) {
        contactName = `${selectedLead.contact_first_name || ''} ${selectedLead.contact_last_name || ''}`.trim()
      }

      setProposalData((prev) => ({
        ...prev,
        company_name: selectedLead.company_name || '',
        contact_name: contactName,
        contact_email: primaryContact?.email || selectedLead.email || '',
        contact_phone: primaryContact?.phone || selectedLead.phone || '',
      }))
    }
  }, [proposalData.customer_id, proposalData.lead_id, entityType, customers, selectedLead])

  // Pre-populate property_type when property/project is selected
  useEffect(() => {
    if (entityType === 'customer' && proposalData.property_id) {
      const selectedProperty = selectedCustomerProperties.find(
        (p) => p.id === proposalData.property_id
      )
      if (selectedProperty) {
        setProposalData((prev) => ({
          ...prev,
          property_type: selectedProperty.property_type || '',
        }))
      }
    } else if (entityType === 'lead' && proposalData.property_id && selectedLead?.projects) {
      let selectedProject: any = null

      if (selectedLead.projects.some((p: any) => p.id === proposalData.property_id)) {
        selectedProject = selectedLead.projects.find((p: any) => p.id === proposalData.property_id)
      } else if (proposalData.property_id.includes('project-')) {
        const projectIndex = parseInt(proposalData.property_id.replace('project-', ''))
        if (projectIndex >= 0 && selectedLead.projects[projectIndex]) {
          selectedProject = selectedLead.projects[projectIndex]
        }
      }

      if (selectedProject) {
        setProposalData((prev) => ({
          ...prev,
          property_type: selectedProject.type || '',
        }))
      }
    }
  }, [proposalData.property_id, entityType, selectedCustomerProperties, selectedLead])

  const fetchCustomerContactInfo = async (customerId: string) => {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('email, phone, contact_first_name, contact_last_name')
        .eq('id', customerId)
        .single()

      if (!error && data) {
        setProposalData((prev) => ({
          ...prev,
          contact_email: data.email || '',
          contact_phone: data.phone || '',
          contact_name: `${data.contact_first_name || ''} ${data.contact_last_name || ''}`.trim(),
        }))
      }
    } catch (error) {
      console.error('Error fetching customer contact info:', error)
    }
  }

  const fetchLeadWithProjects = async (leadId: string) => {
    try {
      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .eq('id', leadId)
        .single()

      if (error) throw error
      setSelectedLead(data)

      // Pre-populate contact info from lead
      const primaryContact = data.contacts && data.contacts[0] ? data.contacts[0] : null

      // Handle contact name
      let contactName = ''
      if (primaryContact) {
        if (primaryContact.first_name || primaryContact.last_name) {
          contactName = `${primaryContact.first_name || ''} ${primaryContact.last_name || ''}`.trim()
        } else if (primaryContact.name) {
          contactName = primaryContact.name
        }
      }
      if (!contactName && (data.contact_first_name || data.contact_last_name)) {
        contactName = `${data.contact_first_name || ''} ${data.contact_last_name || ''}`.trim()
      }

      setProposalData((prev) => ({
        ...prev,
        company_name: data.company_name || '',
        contact_name: contactName,
        contact_email: primaryContact?.email || data.email || '',
        contact_phone: primaryContact?.phone || data.phone || '',
      }))
    } catch (error) {
      console.error('Error fetching lead with projects:', error)
      setSelectedLead(null)
    }
  }

  // Update subtotal and total when line items or tax settings change
  useEffect(() => {
    // For variable quantity recurring items, use unit_price (total per month) instead of total_price (which is 0)
    const lineItemsTotal = lineItems.reduce((sum, item) => {
      // If item is variable quantity AND recurring, use unit_price (represents total per month)
      if (item.is_variable && item.recurrence_type && item.recurrence_type !== 'one_time') {
        return sum + item.unit_price
      }
      // Otherwise use total_price (for fixed quantity items or one-time variable items)
      return sum + item.total_price
    }, 0)

    // Tax calculation based on sales_tax_status
    // Only calculate tax if status is 'taxable', otherwise tax is 0
    const shouldCalculateTax = proposalData.sales_tax_status === 'taxable'
    const taxAmount = shouldCalculateTax ? lineItemsTotal * (proposalData.tax_rate / 100) : 0
    const total = lineItemsTotal + taxAmount

    setProposalData((prev) => ({
      ...prev,
      subtotal: lineItemsTotal,
      tax_amount: taxAmount,
      total_amount: total,
    }))
  }, [lineItems, proposalData.tax_rate, proposalData.sales_tax_status])

  // Line item management functions
  const handleLineItemChange = (index: number, field: string, value: string | number | boolean) => {
    const newLineItems = [...lineItems]
    newLineItems[index] = { ...newLineItems[index], [field]: value }

    // Recalculate total if quantity or unit_price changed (but not if is_variable is true)
    if ((field === 'quantity' || field === 'unit_price') && !newLineItems[index].is_variable) {
      newLineItems[index].total_price =
        Number(newLineItems[index].quantity) * Number(newLineItems[index].unit_price)
    } else if (field === 'is_variable') {
      // When toggling is_variable, update quantity and total accordingly
      if (value) {
        newLineItems[index].total_price = 0
        newLineItems[index].quantity = 0
      } else {
        newLineItems[index].total_price =
          Number(newLineItems[index].quantity) * Number(newLineItems[index].unit_price)
      }
    }

    setLineItems(newLineItems)
  }

  // Helper function to normalize unit_type from service catalog to line item unit_type
  const normalizeUnitType = (catalogUnitType: string): string => {
    const normalized = catalogUnitType.toLowerCase().trim()
    // Map common variations to standard unit_type values
    const unitTypeMap: Record<string, string> = {
      monthly: 'per_month',
      'per month': 'per_month',
      month: 'per_month',
      hourly: 'per_hour',
      'per hour': 'per_hour',
      hour: 'per_hour',
      sqft: 'per_sqft',
      'sq ft': 'per_sqft',
      'square foot': 'per_sqft',
      'square feet': 'per_sqft',
      visit: 'per_visit',
      'per visit': 'per_visit',
      unit: 'per_unit',
      'per unit': 'per_unit',
      flat: 'flat_rate',
      'flat rate': 'flat_rate',
    }
    return unitTypeMap[normalized] || catalogUnitType
  }

  const handleServiceItemSelect = (index: number, serviceItemId: string) => {
    if (!serviceItemId) {
      // Clear service item link
      const newLineItems = [...lineItems]
      newLineItems[index] = {
        ...newLineItems[index],
        service_item_id: undefined,
      }
      setLineItems(newLineItems)
      return
    }

    // Find the selected service item
    let selectedItem: { name: string; unit_type: string; base_price: number } | null = null
    for (const group of serviceItemsGrouped) {
      const item = group.items.find((i) => i.id === serviceItemId)
      if (item) {
        selectedItem = item
        break
      }
    }

    if (selectedItem) {
      const normalizedUnitType = normalizeUnitType(selectedItem.unit_type)
      const newLineItems = [...lineItems]
      newLineItems[index] = {
        ...newLineItems[index],
        service_item_id: serviceItemId,
        description: selectedItem.name, // Auto-populate description
        unit_type: normalizedUnitType, // Auto-populate unit_type (normalized to match dropdown options)
        unit_price: selectedItem.base_price, // Auto-populate unit_price
        total_price: Number(newLineItems[index].quantity) * Number(selectedItem.base_price), // Recalculate total
      }
      setLineItems(newLineItems)
    }
  }

  const addLineItem = () => {
    const newItem: ProposalLineItem = {
      id: `line_item_${Date.now()}`,
      service_item_id: undefined,
      description: '',
      quantity: 1,
      is_variable: false,
      unit_type: 'flat_rate',
      unit_price: 0,
      total_price: 0,
      sort_order: lineItems.length,
      // Default interval settings
      recurrence_type: proposalData.contract_type === 'recurring' ? 'weekly' : 'one_time',
      recurrence_interval: 1,
      interval_unit: '',
      service_type: proposalData.service_type || '',
    }
    setLineItems([...lineItems, newItem])
  }

  const removeLineItem = (index: number) => {
    setLineItems(lineItems.filter((_, i) => i !== index))
  }

  const handlePricingModeChange = (mode: PricingMode) => {
    setProposalData((prev) => ({
      ...prev,
      document_data: { ...prev.document_data, pricingMode: mode },
      billing_method:
        mode === 'rate_card' && (!(prev as any).billing_method || (prev as any).billing_method === 'per_period')
          ? 'actual_usage'
          : (prev as any).billing_method,
    }))
  }

  const mapRateCardToProposalLines = (serviceType: string) =>
    buildRateCardLineItems(serviceType).map((item) => ({
      ...item,
      service_item_id: undefined as string | undefined,
      recurrence_type: 'one_time' as const,
      recurrence_interval: 1,
      interval_unit: '' as const,
      service_type: serviceType || '',
    }))

  const handleSeedRateCard = () => {
    if (!hasRateCardSeed((proposalData as any).service_type)) return
    if (
      lineItems.length > 0 &&
      !window.confirm('Replace existing line items with rate card tiers?')
    ) {
      return
    }
    setLineItems(mapRateCardToProposalLines((proposalData as any).service_type))
  }

  const findExistingDsnyLineIndexes = (): number[] =>
    lineItems
      .map((li, i) =>
        li.service_type === 'maintenance_dsny' ||
        li.service_type === 'dsny_prevailing_wage' ||
        li.service_type === 'dsny' ||
        /dsny|building\s*service|building\s*upkeep|exterior\s*maintenance|hallway/i.test(
          li.description || ''
        )
          ? i
          : -1
      )
      .filter((i) => i >= 0)

  const handleAutoDetectDSNYFrequencies = async () => {
    const siteAddress = resolveProposalSiteAddress({
      entityType,
      propertyId: proposalData.property_id,
      customerProperties: selectedCustomerProperties,
      leadProjects: selectedLead?.projects || [],
      fallbackAddress: proposalRequest?.property_address,
    })
    if (!siteAddress) {
      setDsnyFreqNotice('Please select a property with an address first.')
      return
    }
    setDsnyFreqNotice('')
    setDsnyModalError('')
    setDsnyModalDetected(null)

    const existingIdx = findExistingDsnyLineIndexes()[0]
    if (existingIdx !== undefined && existingIdx >= 0) {
      const existing = lineItems[existingIdx]
      const desc = existing.description || ''
      const isExterior = /exterior\s*maintenance/i.test(desc)
      setDsnyPackageKind(isExterior ? 'exterior_maintenance' : 'building_upkeep')
      const dsnyMatch = desc.match(/dsny[^0-9]*([0-9]+)\s*[x×]/i)
      const hallwayMatch = desc.match(/hallway[^0-9]*([0-9]+)\s*[x×]/i)
      const dsnyVal = dsnyMatch ? parseInt(dsnyMatch[1], 10) : 0
      const hallwayVal = hallwayMatch ? parseInt(hallwayMatch[1], 10) : 0
      setDsnyVisitsPerWeek(dsnyVal || existing.recurrence_interval || 0)
      setHallwayVisitsPerWeek(isExterior ? 0 : hallwayVal || 0)
    } else {
      setDsnyPackageKind('building_upkeep')
      setDsnyVisitsPerWeek(0)
      setHallwayVisitsPerWeek(1)
    }

    setShowDsnyModal(true)
    setDsnyModalLoading(true)
    try {
      const freq = await getDSNYFrequencies(siteAddress)
      if (!freq) {
        setDsnyModalError(
          'No DSNY data found for this address. You can still set custom visit frequencies below.'
        )
        return
      }
      setDsnyModalDetected({
        garbage: freq.garbage || 0,
        recycling: freq.recycling || 0,
        organics: freq.organics || 0,
        total: freq.total || 0,
      })
      if (existingIdx === undefined) {
        setDsnyVisitsPerWeek(freq.total || 0)
      }
    } catch (err) {
      console.error('DSNY frequency fetch failed:', err)
      setDsnyModalError(
        'Could not reach DSNY. You can still set custom visit frequencies below.'
      )
    } finally {
      setDsnyModalLoading(false)
    }
  }

  const confirmDsnyAdd = () => {
    const isExterior = dsnyPackageKind === 'exterior_maintenance'
    const dsny = Math.max(0, Number(dsnyVisitsPerWeek) || 0)
    const hallway = isExterior ? 0 : Math.max(0, Number(hallwayVisitsPerWeek) || 0)
    if (dsny === 0 && hallway === 0) {
      setDsnyModalError(
        isExterior
          ? 'Set at least one DSNY visit per week.'
          : 'Set at least one DSNY or hallway visit per week.'
      )
      return
    }
    try {
      const totalWeeklyVisits = dsny + hallway
      const existingIdxs = findExistingDsnyLineIndexes()
      const remainingItems =
        existingIdxs.length === 0
          ? lineItems
          : lineItems.filter((_, i) => !existingIdxs.includes(i))
      const replaced = existingIdxs.length > 0

      const catalogItem = findDsnyPackageItem(dsnyPackageKind, serviceItemsGrouped)
      const defaultName = isExterior ? 'Exterior Maintenance' : 'Building Upkeep'

      const breakdownParts: string[] = []
      if (dsny > 0) {
        const detectedNote =
          dsnyModalDetected && dsnyModalDetected.total !== dsny
            ? ` (detected ${dsnyModalDetected.total})`
            : ''
        breakdownParts.push(`DSNY pickups ${dsny}×/wk${detectedNote}`)
      }
      if (hallway > 0) {
        breakdownParts.push(`hallway cleaning ${hallway}×/wk`)
      }
      const breakdown = breakdownParts.join(' + ')
      const unitPrice = catalogItem?.base_price || 0
      const baseName = catalogItem?.name || defaultName
      const fullDescription = breakdown ? `${baseName} — ${breakdown}` : baseName

      const newItem: ProposalLineItem = {
        id: `line_item_${Date.now()}_dsny_${Math.random().toString(36).slice(2, 7)}`,
        service_item_id: catalogItem?.id,
        description: fullDescription,
        quantity: 1,
        is_variable: false,
        unit_type: catalogItem?.unit_type || 'per_month',
        unit_price: unitPrice,
        total_price: unitPrice,
        sort_order: remainingItems.length,
        recurrence_type: 'weekly',
        recurrence_interval: totalWeeklyVisits,
        interval_unit: 'days_per_week',
        service_type: isDsnyServiceType(proposalData.service_type)
          ? proposalData.service_type
          : 'maintenance_dsny',
      }

      setLineItems([...remainingItems, newItem])
      setProposalData((prev) => ({
        ...prev,
        contract_type:
          prev.contract_type === 'recurring'
            ? prev.contract_type
            : ('recurring' as '' | 'one_time' | 'recurring'),
        dsny_integration: dsny > 0 ? true : prev.dsny_integration,
        service_type:
          prev.service_type ||
          (isDsnyServiceType(proposalData.service_type)
            ? proposalData.service_type
            : 'maintenance_dsny'),
      }))

      const priceLabel = unitPrice
        ? `$${unitPrice}/${newItem.unit_type.replace('per_', '')}`
        : 'price TBD'
      const splitNote = isExterior
        ? 'The contract will schedule DSNY pickup days (no hallway).'
        : 'The contract will split this into separate scheduled services (DSNY days and hallway day).'
      setDsnyFreqNotice(
        `${replaced ? 'Updated' : 'Added'} "${fullDescription}" — ${totalWeeklyVisits}×/wk total (${priceLabel}). ${splitNote}`
      )
      setShowDsnyModal(false)
    } catch (err) {
      console.error('DSNY add failed:', err)
      setDsnyModalError('Could not add the line item. Please try again.')
    }
  }

  const fetchProposalData = async () => {
    try {
      const { data, error } = await supabase
        .from('proposals')
        .select('*, project_address')
        .eq('id', id)
        .single()

      if (error) throw error

      setProposalRecord(data)

      // Determine entity type based on which ID is present
      // If customer_id exists, use customer (even if lead_id also exists - lead was converted)
      const hasCustomerId = !!data.customer_id
      const hasLeadId = !!data.lead_id && data.lead_id !== ''

      if (hasCustomerId) {
        setEntityType('customer')
      } else if (hasLeadId) {
        setEntityType('lead')
      } else {
        // Default to customer if neither exists (shouldn't happen, but safety check)
        setEntityType('customer')
      }

      // If it's a lead, fetch the lead and projects first to properly set property_id
      let resolvedPropertyId = data.property_id || ''

      if (hasLeadId && !hasCustomerId) {
        // It's a lead proposal - need to fetch lead and match project
        const { data: leadData, error: leadError } = await supabase
          .from('leads')
          .select('*')
          .eq('id', data.lead_id)
          .single()

        if (!leadError && leadData) {
          setSelectedLead(leadData)

          // Try to match the project
          if (leadData.projects && leadData.projects.length > 0) {
            // Method 1: Match by project_address if stored
            if (data.project_address) {
              const projectIndex = leadData.projects.findIndex(
                (p: any) => p.address === data.project_address
              )
              if (projectIndex >= 0) {
                resolvedPropertyId = leadData.projects[projectIndex].id || `project-${projectIndex}`
              }
            }
            // Method 2: Match by property_id if it contains project-${index}
            else if (
              data.property_id &&
              typeof data.property_id === 'string' &&
              data.property_id.includes('project-')
            ) {
              const projectIndex = parseInt(data.property_id.replace('project-', ''))
              if (projectIndex >= 0 && leadData.projects[projectIndex]) {
                resolvedPropertyId = leadData.projects[projectIndex].id || `project-${projectIndex}`
              }
            }
            // Method 3: Try to match by project ID
            else if (
              data.property_id &&
              typeof data.property_id === 'string' &&
              !data.property_id.match(
                /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
              )
            ) {
              const projectIndex = leadData.projects.findIndex(
                (p: any) => p.id === data.property_id
              )
              if (projectIndex >= 0) {
                resolvedPropertyId = leadData.projects[projectIndex].id || `project-${projectIndex}`
              }
            }
          }
        }
      }

      const originalStatusValue = data.status || 'draft'
      setOriginalStatus(originalStatusValue)

      const contractType = (data.contract_type || 'one_time') as ContractType
      setProposalCreatedAt(
        data.created_at ? String(data.created_at).split('T')[0] : todayDateInputValue()
      )

      setProposalData({
        title: data.title || '',
        description: data.description || '',
        template_type: data.template_type || '',
        service_type: data.service_type || '',
        contract_type: data.contract_type || 'one_time',
        customer_id: data.customer_id || '',
        lead_id: data.lead_id || null,
        property_id: resolvedPropertyId,
        property_type: '', // Will be populated from property/project table
        company_name: '', // Will be populated from customer/lead table
        contact_name: '', // Will be populated from customer/lead table
        contact_email: '', // Will be populated from customer/lead table
        contact_phone: '', // Will be populated from customer/lead table
        total_amount: data.total || 0,
        subtotal: data.subtotal || 0,
        tax_rate: data.tax_rate || 0,
        tax_amount: data.tax_amount || 0,
        sales_tax_status: data.sales_tax_status || '',
        payment_terms: data.payment_terms || 30,
        valid_until: data.valid_until || '',
        terms_and_conditions: data.terms_and_conditions || '',
        status: originalStatusValue as 'draft' | 'sent' | 'viewed' | 'approved' | 'rejected' | 'expired' | 'converted',
        // New fields
        start_date: data.start_date || '',
        end_date: data.end_date || '',
        billing_frequency: normalizeBillingFrequency(
          contractType,
          data.billing_frequency || ''
        ),
        billing_day_of_month: Number((data as any).billing_day_of_month) || 1,
        monthly_amount: data.monthly_amount || 0,
        late_fee_percentage: data.late_fee_percentage || 0,
        customer_notes: data.customer_notes || '',
        dsny_integration: data.dsny_integration || false,
        dsny_intent_message: data.dsny_intent_message || '',
        scope_of_work: data.scope_of_work || '',
        document_data:
          data.document_data && Object.keys(data.document_data).length > 0
            ? data.document_data
            : buildDefaultDocumentData(data.service_type),
        is_locked: data.is_locked || false,
        prices_locked: data.prices_locked || false,
      })

      try {
        const milestones = await fetchProposalMilestones(data.id)
        setBillingMilestones(milestones)
      } catch (milestoneErr) {
        console.error('Error loading billing milestones:', milestoneErr)
        setBillingMilestones([])
      }

      // If lead was converted but proposal still has lead_id, update to use customer
      // This handles the case where the proposal needs to be updated after conversion
      if (hasLeadId && hasCustomerId) {
        // Lead was converted, proposal should use customer mode
        setEntityType('customer')
        // Fetch customer properties if needed
        if (data.customer_id) {
          fetchPropertiesForCustomer(data.customer_id)
        }
      }

      // Fetch notes - handle both JSONB array and JSON string
      let parsedNotes: ProposalNote[] = []
      if (data.notes) {
        if (Array.isArray(data.notes)) {
          parsedNotes = data.notes
        } else if (typeof data.notes === 'string') {
          try {
            const parsed = JSON.parse(data.notes)
            if (Array.isArray(parsed)) {
              parsedNotes = parsed
            }
          } catch (e) {
            console.warn('Failed to parse notes as JSON:', e)
          }
        }
      }
      setNotes(parsedNotes)
      console.log('Fetched notes:', parsedNotes)

      // Fetch line items
      await fetchProposalLineItems()
    } catch (error) {
      console.error('Error fetching proposal data:', error)
      setNotification({
        type: 'error',
        message: 'Error loading proposal: ' + (error as any).message,
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setLoadingData(false)
    }
  }

  const fetchProposalLineItems = async () => {
    if (!id) return
    try {
      const { data, error } = await supabase
        .from('proposal_line_items')
        .select('*')
        .eq('proposal_id', id)
        .order('sort_order', { ascending: true })

      if (error) {
        // Table might not exist yet, that's okay
        console.warn('Error fetching proposal line items (table may not exist):', error)
        return
      }

      if (data) {
        setLineItems(
          data.map((item) => ({
            id: item.id,
            service_item_id: item.service_item_id || undefined,
            description: item.description || '',
            quantity: item.quantity || 1,
            is_variable: item.is_variable || (item.quantity === 0), // Support legacy: quantity=0 means variable
            // Normalize legacy/catalog-style values like "monthly" → "per_month"
            // so the dropdown picks the right option (otherwise it silently
            // falls back to the first option and looks like "Flat Rate").
            unit_type: normalizeUnitType(item.unit_type || 'flat_rate'),
            unit_price: item.unit_price || 0,
            total_price: item.total_price || 0,
            sort_order: item.sort_order || 0,
            // New interval fields with defaults
            recurrence_type: item.recurrence_type || 'one_time',
            recurrence_interval: item.recurrence_interval || 1,
            interval_unit: item.interval_unit || '',
            service_type: item.service_type || proposalData.service_type || '',
            conversion_status: item.conversion_status || undefined,
          }))
        )
      }
    } catch (error) {
      console.error('Error fetching proposal line items:', error)
    }
  }

  const fetchCustomers = async () => {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('id, company_name, contact_first_name, contact_last_name')
        .in('status', ['onboarding', 'active']) // Only show open customers (onboarding or active, not terminated)
        .order('company_name')

      if (error) throw error
      setCustomers(data || [])
    } catch (error) {
      console.error('Error fetching customers:', error)
    }
  }

  const fetchPropertiesForCustomer = async (customerId: string) => {
    try {
      const { data, error } = await supabase
        .from('properties')
        .select('id, name, address, customer_id, property_type')
        .eq('customer_id', customerId)
        .order('name')

      if (error) throw error
      setSelectedCustomerProperties(data || [])
    } catch (error) {
      console.error('Error fetching properties:', error)
    }
  }

  const handleOpenCreateModal = () => {
    setNewPropertyData({
      name: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      property_type: '',
    })
    setShowCreatePropertyModal(true)
  }

  const handleCreateProperty = async () => {
    if (!proposalData.customer_id) {
      setNotification({
        type: 'error',
        message: 'Please select a customer first',
      })
      setTimeout(() => setNotification(null), 5000)
      return
    }

    if (!newPropertyData.name && !newPropertyData.address) {
      setNotification({
        type: 'error',
        message: 'Please enter at least a name or address',
      })
      setTimeout(() => setNotification(null), 5000)
      return
    }

    setCreatingProperty(true)
    try {
      const insertData: Record<string, string | null> = {
        customer_id: proposalData.customer_id,
        name: newPropertyData.name || newPropertyData.address || 'New Property',
        address: newPropertyData.address || null,
        city: newPropertyData.city || null,
        state: newPropertyData.state || null,
        zip_code: newPropertyData.zip || null,
        property_type: newPropertyData.property_type || null,
      }

      const { data: property, error } = await supabase
        .from('properties')
        .insert([insertData])
        .select()
        .single()

      if (error) throw error

      await fetchPropertiesForCustomer(proposalData.customer_id)

      setProposalData((prev) => ({
        ...prev,
        property_id: property.id,
        property_type: property.property_type || '',
      }))

      setShowCreatePropertyModal(false)
      setNewPropertyData({
        name: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        property_type: '',
      })

      setNotification({
        type: 'success',
        message: 'Property created successfully!',
      })
      setTimeout(() => setNotification(null), 3000)
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      console.error('Error creating property:', error)
      setNotification({
        type: 'error',
        message: 'Error creating property: ' + message,
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setCreatingProperty(false)
    }
  }

  const handleCreateProject = async () => {
    if (!proposalData.lead_id) {
      setNotification({
        type: 'error',
        message: 'Please select a lead first',
      })
      setTimeout(() => setNotification(null), 5000)
      return
    }

    if (!newPropertyData.address) {
      setNotification({
        type: 'error',
        message: 'Please enter an address for the project',
      })
      setTimeout(() => setNotification(null), 5000)
      return
    }

    setCreatingProperty(true)
    try {
      const { data: currentLead, error: fetchError } = await supabase
        .from('leads')
        .select('projects')
        .eq('id', proposalData.lead_id)
        .single()

      if (fetchError) throw fetchError

      const newProject = {
        id: `project-${Date.now()}`,
        address: newPropertyData.address,
        city: newPropertyData.city || '',
        state: newPropertyData.state || '',
        zip: newPropertyData.zip || '',
        type: newPropertyData.property_type || '',
      }

      const updatedProjects = [...(currentLead.projects || []), newProject]

      const { error: updateError } = await supabase
        .from('leads')
        .update({ projects: updatedProjects })
        .eq('id', proposalData.lead_id)

      if (updateError) throw updateError

      await fetchLeadWithProjects(proposalData.lead_id)

      setProposalData((prev) => ({
        ...prev,
        property_id: newProject.id,
        property_type: newProject.type || '',
      }))

      setShowCreatePropertyModal(false)
      setNewPropertyData({
        name: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        property_type: '',
      })

      setNotification({
        type: 'success',
        message: 'Project created successfully!',
      })
      setTimeout(() => setNotification(null), 3000)
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      console.error('Error creating project:', error)
      setNotification({
        type: 'error',
        message: 'Error creating project: ' + message,
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setCreatingProperty(false)
    }
  }

  const fetchProposalRequest = async () => {
    if (!proposalRecord) return

    setLoadingRequest(true)
    try {
      setProposalRequest(resolveProposalRequestFromProposal(proposalRecord))
    } catch (error) {
      console.error('Error resolving proposal request:', error)
      setProposalRequest(null)
    } finally {
      setLoadingRequest(false)
    }
  }

  const addNote = async () => {
    if (!newNote.trim() || !id) return

    setSavingNote(true)
    try {
      const noteToAdd: ProposalNote = {
        timestamp: new Date().toISOString(),
        note: newNote.trim(),
      }

      const updatedNotes = [noteToAdd, ...notes]

      console.log('Saving notes:', updatedNotes)

      const { error } = await supabase
        .from('proposals')
        .update({ notes: updatedNotes }) // Supabase should handle JSONB arrays automatically
        .eq('id', id)

      if (error) throw error

      setNotes(updatedNotes)
      setNewNote('')

      // Log activity
      await logActivity({
        activity_type: 'note_added',
        entity_type: 'proposal',
        entity_id: id!,
        description: 'Note added to proposal',
      })
    } catch (error) {
      console.error('Error adding note:', error)
      setNotification({
        type: 'error',
        message: 'Error adding note',
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setSavingNote(false)
    }
  }

  const removeNote = async (index: number) => {
    if (!id) return

    setSavingNote(true)
    try {
      const updatedNotes = notes.filter((_, i) => i !== index)

      const { error } = await supabase
        .from('proposals')
        .update({ notes: updatedNotes.length > 0 ? updatedNotes : null })
        .eq('id', id)

      if (error) throw error

      setNotes(updatedNotes)

      // Log activity
      await logActivity({
        activity_type: 'note_removed',
        entity_type: 'proposal',
        entity_id: id!,
        description: 'Note removed from proposal',
      })
    } catch (error) {
      console.error('Error removing note:', error)
      setNotification({
        type: 'error',
        message: 'Error removing note',
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setSavingNote(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.ctrlKey && e.key === 'Enter') {
      addNote()
    }
  }

  // Build the agreement state used by the consistency rules engine
  const agreementState: AgreementState = React.useMemo(() => {
    const selectedProp = (selectedCustomerProperties || []).find(
      (p: any) => p.id === proposalData.property_id
    )
    return {
      contract_type: (proposalData.contract_type as any) || '',
      service_type: (proposalData as any).service_type,
      billing_frequency: (proposalData as any).billing_frequency,
      billing_method: (proposalData as any).billing_method,
      payment_method: (proposalData as any).payment_method,
      template_type: proposalData.template_type as any,
      start_date: proposalData.start_date || null,
      end_date: proposalData.end_date || null,
      property_address: selectedProp?.address || '',
      line_items: (lineItems || []).map((li: any) => ({
        id: li.id,
        service_item_id: li.service_item_id,
        description: li.description,
        quantity: li.quantity,
        is_variable: li.is_variable,
        unit_type: li.unit_type,
        unit_price: li.unit_price,
        total_price: li.total_price,
        sort_order: li.sort_order,
        recurrence_type: li.recurrence_type,
        recurrence_interval: li.recurrence_interval,
        interval_unit: li.interval_unit,
        service_type: li.service_type,
      })),
      billing_milestones: billingMilestones,
      dsny_integration: proposalData.dsny_integration,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalData, lineItems, billingMilestones, selectedCustomerProperties])

  const consistencyIssues = React.useMemo(
    () => checkConsistency(agreementState, 'proposal'),
    [agreementState]
  )

  const handleNavigateIssue = (target: IssueTarget) => {
    if (!target) return
    let local: 'overview' | 'items' | 'billing' | 'notes' | 'request' = activeTab
    if (target.tab === 'overview') local = 'overview'
    else if (target.tab === 'services') local = 'items'
    else if (target.tab === 'billing') local = 'billing'
    else if (target.tab === 'notes') local = 'notes'
    if (local !== activeTab) setActiveTab(local)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Phase 5: enforce blockers before save
    const { blockers } = splitIssues(consistencyIssues)
    if (blockers.length > 0) {
      const first = blockers[0]
      setNotification({
        type: 'error',
        message: `Please fix ${blockers.length} issue${blockers.length === 1 ? '' : 's'} before saving. First: ${first.message}`,
      })
      setTimeout(() => setNotification(null), 6000)
      handleNavigateIssue(first.target)
      return
    }

    console.log('ProposalEdit submit check:', {
      status: proposalData.status,
      originalStatus: originalStatus,
      entityType: entityType,
      lead_id: proposalData.lead_id,
      customer_id: proposalData.customer_id,
      hasSelectedLead: !!selectedLead,
      needsConversion: proposalData.status === 'approved' &&
        originalStatus !== 'approved' &&
        proposalData.lead_id &&
        proposalData.lead_id !== '' &&
        !proposalData.customer_id,
    })

    // If changing to "approved" and proposal is linked to a lead (not a customer), show convert modal
    // Check both entityType and direct lead_id/customer_id to be safe
    const isApproving = proposalData.status === 'approved' && originalStatus !== 'approved'
    const hasLead = proposalData.lead_id && proposalData.lead_id !== ''
    const hasCustomer = !!proposalData.customer_id

    if (isApproving && hasLead && !hasCustomer) {
      console.log('Proposal needs conversion - fetching lead if not loaded')

      // If lead isn't loaded, fetch it
      if (!selectedLead && proposalData.lead_id) {
        try {
          const { data: leadData, error: leadError } = await supabase
            .from('leads')
            .select('*')
            .eq('id', proposalData.lead_id)
            .single()

          if (!leadError && leadData) {
            setSelectedLead(leadData)
            populateConversionData(leadData)
            setShowConvertModal(true)
            return
          } else {
            console.error('Error fetching lead for conversion:', leadError)
          }
        } catch (error) {
          console.error('Error fetching lead:', error)
        }
      } else if (selectedLead) {
        console.log('Showing convert modal in ProposalEdit')
        populateConversionData(selectedLead)
        setShowConvertModal(true)
        return
      }
    }

    console.log('Submitting proposal normally')
    // Otherwise, proceed with normal submission
    await submitProposal()
  }

  const submitProposal = async () => {
    setLoading(true)

    try {
      // Validate property_id - only use it if it's a valid UUID (for customers)
      // For leads with projects, property_id contains project identifier, not a UUID
      let propertyIdForDB = null
      if (entityType === 'customer' && proposalData.property_id) {
        // Check if it's a valid UUID (not starting with "project-")
        if (!proposalData.property_id.startsWith('project-')) {
          propertyIdForDB = proposalData.property_id
        }
      }

      // For leads, store project reference for later conversion
      let projectAddress = null
      if (entityType === 'lead' && proposalData.property_id && selectedLead?.projects) {
        // Extract project info for matching after conversion
        if (proposalData.property_id.includes('project-')) {
          const projectIndex = parseInt(proposalData.property_id.replace('project-', ''))
          if (projectIndex >= 0 && selectedLead.projects[projectIndex]?.address) {
            projectAddress = selectedLead.projects[projectIndex].address
          }
        } else {
          // Try to find by project ID
          const project = selectedLead.projects.find((p: any) => p.id === proposalData.property_id)
          if (project?.address) {
            projectAddress = project.address
          }
        }
      }

      const proposalUpdate: any = {
        title: proposalData.title,
        customer_id:
          entityType === 'customer' && proposalData.customer_id ? proposalData.customer_id : null,
        lead_id: entityType === 'lead' && proposalData.lead_id ? proposalData.lead_id : null,
        property_id: propertyIdForDB,
        subtotal: proposalData.subtotal,
        tax_rate: proposalData.tax_rate,
        tax_amount: proposalData.tax_amount,
        sales_tax_status: proposalData.sales_tax_status || null,
        total: proposalData.total_amount,
        valid_until: proposalData.valid_until || null,
        status: proposalData.status,
        template_type: proposalData.template_type || null,
        // Contract-alignment fields (previously dropped on save - bug fix H1)
        service_type: (proposalData as any).service_type || null,
        contract_type: proposalData.contract_type || null,
        start_date: proposalData.start_date || null,
        end_date: proposalData.end_date || null,
        billing_frequency: defaultBillingFrequencyForSave(
          proposalData.contract_type || 'one_time',
          proposalData.billing_frequency
        ),
        billing_day_of_month: Number((proposalData as any).billing_day_of_month) || 1,
        monthly_amount: proposalData.monthly_amount || null,
        late_fee_percentage: proposalData.late_fee_percentage || null,
        customer_notes: proposalData.customer_notes || null,
        dsny_integration: proposalData.dsny_integration ?? false,
        dsny_intent_message: proposalData.dsny_intent_message || null,
        // Billing/payment method (added by 20260303 migration)
        billing_method:
          proposalData.billing_frequency === 'progress'
            ? 'progress'
            : (proposalData as any).billing_method || null,
        payment_method: (proposalData as any).payment_method || null,
        scope_of_work: proposalData.scope_of_work || null,
        document_data: proposalData.document_data || {},
        notes: notes.length > 0 ? notes : null,
        updated_at: new Date().toISOString(),
      }

      // Store project address for lead conversion mapping (if column exists)
      if (projectAddress) {
        proposalUpdate.project_address = projectAddress
      }

      // Add description if provided (column should exist after running migration)
      if (proposalData.description) {
        proposalUpdate.description = proposalData.description
      }

      // Add payment_terms conditionally (may not exist in all schemas)
      if (proposalData.payment_terms) {
        proposalUpdate.payment_terms = proposalData.payment_terms
      }

      // Add fields conditionally - only include if column exists and has a value
      // Some schemas use 'terms_and_conditions', others use 'terms_conditions'
      if (proposalData.terms_and_conditions && proposalData.terms_and_conditions.trim()) {
        proposalUpdate.terms_and_conditions = proposalData.terms_and_conditions
      }

      // Note: We don't store company_name, contact_name, contact_email, contact_phone, or property_type
      // directly on proposals. This data should be fetched from the related customer/lead and property tables
      // to maintain data integrity and avoid duplication.

      const { error } = await supabase.from('proposals').update(proposalUpdate).eq('id', id)

      if (error) throw error

      if (id) {
        if (proposalData.billing_frequency === 'progress') {
          await saveProposalMilestones(id, billingMilestones, proposalData.total_amount)
        } else {
          await saveProposalMilestones(id, [], proposalData.total_amount)
        }
      }

      // Update lead stage to 'proposal_sent' if proposal status is changed to 'sent' and linked to a lead
      if (proposalData.status === 'sent' && proposalData.lead_id) {
        try {
          const { error: leadUpdateError } = await supabase
            .from('leads')
            .update({ stage: 'proposal_sent' })
            .eq('id', proposalData.lead_id)

          if (leadUpdateError) {
            console.error('Error updating lead stage:', leadUpdateError)
          } else {
            console.log('✅ Lead stage updated to proposal_sent:', proposalData.lead_id)
          }
        } catch (leadError) {
          console.error('Error updating lead stage:', leadError)
          // Don't fail proposal update if lead update fails
        }
      }

      // Delete existing line items
      await supabase.from('proposal_line_items').delete().eq('proposal_id', id!)

      // Insert new line items if any
      if (lineItems.length > 0 && lineItems.some((item) => item.description.trim())) {
        const lineItemsToInsert = lineItems
          .filter((item) => item.description.trim())
          .map((item: any) => ({
            proposal_id: id!,
            service_item_id: item.service_item_id || null,
            description: item.description,
            quantity: item.is_variable ? 0 : item.quantity,
            is_variable: item.is_variable || false,
            unit_type: item.unit_type,
            unit_price: item.unit_price,
            total_price: item.is_variable ? 0 : item.total_price,
            sort_order: item.sort_order,
            // Recurrence fields (previously dropped on save - bug fix H2)
            recurrence_type: item.recurrence_type || null,
            recurrence_interval: item.recurrence_interval || null,
            interval_unit: item.interval_unit || null,
            service_type: item.service_type || null,
          }))

        if (lineItemsToInsert.length > 0) {
          const { error: lineItemsError } = await supabase
            .from('proposal_line_items')
            .insert(lineItemsToInsert)

          if (lineItemsError) throw lineItemsError
        }
      }

      // Log activity
      await logActivity({
        activity_type: ActivityTypes.PROPOSAL_UPDATED,
        entity_type: 'proposal',
        entity_id: id!,
        description: `Proposal "${proposalData.title}" updated`,
        metadata: {
          customer_id: proposalData.customer_id,
          property_id: proposalData.property_id,
        },
      })

      await logActivityOnLinkedParents({
        parents: {
          lead_id: proposalData.lead_id,
          customer_id: proposalData.customer_id,
          property_id: propertyIdForDB,
        },
        activity_type: ActivityTypes.PROPOSAL_UPDATED,
        description: `Proposal "${proposalData.title}" updated`,
        metadata: {
          child_entity_type: 'proposal',
          child_entity_id: id,
        },
      })

      setNotification({
        type: 'success',
        message: 'Proposal updated successfully!',
      })
      setTimeout(() => {
        if (returnTo) {
          navigate(decodeURIComponent(returnTo))
        } else {
          navigate(`/proposals/view/${id}`)
        }
      }, 1500)
    } catch (error) {
      console.error('Error updating proposal:', error)
      setNotification({
        type: 'error',
        message: 'Error updating proposal: ' + (error as any).message,
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setLoading(false)
    }
  }

  const populateConversionData = (lead: any) => {
    const primaryContact = lead.contacts && lead.contacts[0] ? lead.contacts[0] : null

    // Handle contact name - combine first/last or use name field
    let contactName = ''
    if (primaryContact) {
      if (primaryContact.name) {
        contactName = primaryContact.name
      } else if (primaryContact.first_name || primaryContact.last_name) {
        contactName = `${primaryContact.first_name || ''} ${primaryContact.last_name || ''}`.trim()
      }
    }
    if (!contactName && (lead.contact_first_name || lead.contact_last_name)) {
      contactName = `${lead.contact_first_name || ''} ${lead.contact_last_name || ''}`.trim()
    }

    // Get all contacts
    const allContacts = lead.contacts && Array.isArray(lead.contacts) && lead.contacts.length > 0
      ? lead.contacts.map((c: any) => ({
          name: c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || '',
          phone: c.phone || '',
          cell: c.cell || '',
          email: c.email || '',
        }))
      : []

    // Get all projects with separate address components
    const properties =
      lead.projects && lead.projects.length > 0
        ? lead.projects.map((p: any) => ({
            name: p.address || 'Property',
            address: p.address || '',
            address_line_2: p.address_line_2 || '',
            city: p.city || '',
            state: p.state || '',
            zip: p.zip || '',
            type: p.type || '',
            unit_count: p.unit_count || '',
            work_type: p.work_type || '',
            notes: p.notes || '',
          }))
        : []

    setConversionData({
      company_name: lead.company_name || '',
      company_phone: lead.company_phone || '',
      company_email: lead.company_email || '',
      company_website: lead.company_website || '',
      company_address: lead.company_address || lead.address || '',
      company_address_line_2: lead.company_address_line_2 || '',
      company_city: lead.company_city || lead.city || '',
      company_state: lead.company_state || lead.state || '',
      company_zip: lead.company_zip || lead.zip_code || lead.zip || '',
      contact_name: contactName,
      email: primaryContact?.email || lead.email || lead.company_email || '',
      phone: primaryContact?.phone || lead.phone || lead.company_phone || '',
      cell: primaryContact?.cell || '',
      contacts: allContacts,
      properties: properties,
    })
  }

  const handleConversionFormChange = (field: string, value: any) => {
    setConversionData((prev) => ({ ...prev, [field]: value }))
  }

  const handlePropertyChange = (index: number, field: string, value: string) => {
    setConversionData((prev) => ({
      ...prev,
      properties: prev.properties.map((p, i) => (i === index ? { ...p, [field]: value } : p)),
    }))
  }

  const addProperty = () => {
    setConversionData((prev) => ({
      ...prev,
      properties: [
        ...prev.properties,
        { name: '', address: '', address_line_2: '', city: '', state: '', zip: '', type: '', unit_count: '', work_type: '', notes: '' },
      ],
    }))
  }

  const removeProperty = (index: number) => {
    setConversionData((prev) => ({
      ...prev,
      properties: prev.properties.filter((_, i) => i !== index),
    }))
  }

  const ensureProposalConverted = async (): Promise<boolean> => {
    // Check if proposal is linked to a lead (not a customer)
    if (proposalData.lead_id && !proposalData.customer_id) {
      // Need to convert lead to customer
      if (!selectedLead) {
        // Fetch lead if not loaded
        try {
          const { data: leadData, error: leadError } = await supabase
            .from('leads')
            .select('*')
            .eq('id', proposalData.lead_id)
            .single()

          if (!leadError && leadData) {
            setSelectedLead(leadData)
            populateConversionData(leadData)
            // Show conversion modal
            setShowConvertModal(true)
            return false // Conversion not completed yet
          } else {
            console.error('Error fetching lead for conversion:', leadError)
            alert('Error: Could not load lead information')
            return false
          }
        } catch (error) {
          console.error('Error fetching lead:', error)
          alert('Error: Could not load lead information')
          return false
        }
      } else {
        // Lead already loaded, show conversion modal
        populateConversionData(selectedLead)
        setShowConvertModal(true)
        return false // Conversion not completed yet
      }
    }
    // Already has a customer or no lead, ready to proceed
    return true
  }

  const handleCreateContract = async () => {
    if (!id) return

    const returnToUrl = encodeURIComponent(`/proposals/edit/${id}`)
    // If already linked to customer/property, just navigate (don't approve yet)
    if (proposalData.customer_id && !proposalData.lead_id) {
      // Already linked to customer - navigate to contract creation
      // Proposal will be approved when contract is successfully created
      navigate(`/contracts/create?proposalId=${id}&returnTo=${returnToUrl}`)
      return
    }

    // Otherwise, ensure conversion happens first
    const ready = await ensureProposalConverted()
    if (!ready) {
      // Conversion modal is showing, store action to perform after conversion
      setPendingAction('create-contract')
      return
    }

    // After conversion, navigate to contract creation
    // Proposal will be approved when contract is successfully created
    navigate(`/contracts/create?proposalId=${id}&returnTo=${returnToUrl}`)
  }

  const handleCreateJob = async () => {
    if (!id) return

    const returnToUrl = encodeURIComponent(`/proposals/edit/${id}`)
    // If already linked to customer/property, just navigate (don't approve yet)
    if (proposalData.customer_id && !proposalData.lead_id) {
      // Already linked to customer - navigate to job creation
      // Proposal will be approved when job is successfully created
      navigate(`/jobs/create?proposalId=${id}&returnTo=${returnToUrl}`)
      return
    }

    // Otherwise, ensure conversion happens first
    const ready = await ensureProposalConverted()
    if (!ready) {
      // Conversion modal is showing, store action to perform after conversion
      setPendingAction('create-job')
      return
    }

    // After conversion, navigate to job creation
    // Proposal will be approved when job is successfully created
    navigate(`/jobs/create?proposalId=${id}&returnTo=${returnToUrl}`)
  }

  const ensureProposalApproved = async () => {
    if (!id || !proposalData) return

    try {
      // Only update if status is not already approved
      if (proposalData.status !== 'approved') {
        const { error } = await supabase
          .from('proposals')
          .update({ status: 'approved' })
          .eq('id', id)

        if (error) throw error

        // Update local state
        setProposalData({
          ...proposalData,
          status: 'approved',
        })

        // Log activity
        await logActivity({
          activity_type: ActivityTypes.PROPOSAL_UPDATED,
          entity_type: 'proposal',
          entity_id: id,
          description: 'Proposal approved for contract/job creation',
          metadata: {
            status: 'approved',
          },
        })
      }
    } catch (error) {
      console.error('Error updating proposal:', error)
      setNotification({
        type: 'error',
        message: 'Error updating proposal: ' + (error as any).message,
      })
      setTimeout(() => setNotification(null), 5000)
    }
  }

  const handleConfirmConversion = async () => {
    if (!selectedLead || !id || !proposalData.lead_id) return

    try {
      setConverting(true)

      // Split contact_name back to first_name and last_name for database
      const nameParts = conversionData.contact_name.trim().split(' ')
      const contactFirstName = nameParts[0] || ''
      const contactLastName = nameParts.slice(1).join(' ') || ''

      // Update primary contact in contacts array
      const primaryContact = selectedLead.contacts && selectedLead.contacts[0] ? selectedLead.contacts[0] : null
      const updatedContacts = conversionData.contacts.length > 0
        ? conversionData.contacts.map((c, idx) => {
            if (idx === 0 && primaryContact) {
              // Update primary contact with new data
              return {
                ...primaryContact,
                name: conversionData.contact_name,
                first_name: contactFirstName,
                last_name: contactLastName,
                email: conversionData.email,
                phone: conversionData.phone,
                cell: conversionData.cell,
              }
            }
            return {
              ...c,
              first_name: c.name.split(' ')[0] || '',
              last_name: c.name.split(' ').slice(1).join(' ') || '',
            }
          })
        : primaryContact ? [{
            ...primaryContact,
            name: conversionData.contact_name,
            first_name: contactFirstName,
            last_name: contactLastName,
            email: conversionData.email,
            phone: conversionData.phone,
            cell: conversionData.cell,
          }] : []

      // First update the lead with the new data
      const { error: leadUpdateError } = await supabase
        .from('leads')
        .update({
          company_name: conversionData.company_name,
          company_phone: conversionData.company_phone,
          company_email: conversionData.company_email,
          company_website: conversionData.company_website,
          company_address: conversionData.company_address,
          company_address_line_2: conversionData.company_address_line_2,
          company_city: conversionData.company_city,
          company_state: conversionData.company_state,
          company_zip: conversionData.company_zip,
          contact_first_name: contactFirstName,
          contact_last_name: contactLastName,
          email: conversionData.email,
          phone: conversionData.phone,
          contacts: updatedContacts,
          projects: conversionData.properties.map((prop) => ({
            address: prop.address,
            address_line_2: prop.address_line_2,
            city: prop.city,
            state: prop.state,
            zip: prop.zip,
            type: prop.type,
            unit_count: prop.unit_count,
            work_type: prop.work_type,
            notes: prop.notes,
          })),
        })
        .eq('id', proposalData.lead_id)

      if (leadUpdateError) {
        console.error('Error updating lead:', leadUpdateError)
        throw leadUpdateError
      }

      // Now convert with the updated lead data
      // This function also marks the lead as 'won' and links proposal to customer/property
      const customerId = await convertLeadToCustomer(proposalData.lead_id)

      // Fetch the first property for this customer to link to the proposal
      const { data: customerProperties } = await supabase
        .from('properties')
        .select('id')
        .eq('customer_id', customerId)
        .limit(1)

      const propertyId = customerProperties && customerProperties.length > 0 ? customerProperties[0].id : null

      // Update proposal data to reflect customer instead of lead
      // Don't update status to approved yet - that will happen when job/contract is created
      setProposalData({
        ...proposalData,
        customer_id: customerId,
        lead_id: null, // Clear lead_id after conversion
        property_id: propertyId || proposalData.property_id, // Update property_id if we found one
        // Keep existing status - don't approve until job/contract is created
      })
      setEntityType('customer')
      // Keep original status - don't change it

      // Also directly update the proposal in the database to ensure it's linked
      // But don't change the status - that will happen when job/contract is created
      const proposalUpdate: any = {
        customer_id: customerId,
        lead_id: null,
        // Don't update status here - only update when job/contract is actually created
      }

      if (propertyId) {
        proposalUpdate.property_id = propertyId
      }

      await supabase
        .from('proposals')
        .update(proposalUpdate)
        .eq('id', id)

      console.log('Proposal updated with customer_id:', customerId, 'property_id:', propertyId)

      // Don't call submitProposal() here - that would update the status
      // The proposal will be approved when the job/contract is actually created

      // Check if there's a pending action to perform after conversion
      if (pendingAction === 'create-contract') {
        setPendingAction(null)
        setShowConvertModal(false)
        navigate(`/contracts/create?proposalId=${id}`)
        return
      } else if (pendingAction === 'create-job') {
        setPendingAction(null)
        setShowConvertModal(false)
        navigate(`/jobs/create?proposalId=${id}`)
        return
      }

      setShowConvertModal(false)

      // Show the "Create Job" modal
      setShowCreateJobModal(true)
    } catch (error) {
      console.error('Error converting lead:', error)
      alert('Error converting lead: ' + (error as any).message)
    } finally {
      setConverting(false)
    }
  }

  if (loadingData) {
    return (
      <MobilePage className="flex items-center justify-center min-h-[400px]">
        <p className="text-gray-600">Loading...</p>
      </MobilePage>
    )
  }

  // Admin override handler
  const handleUnlockProposal = async () => {
    const reason = prompt('Please provide a reason for unlocking this approved proposal:')
    if (!reason) return

    try {
      const { error } = await supabase
        .from('proposals')
        .update({
          is_locked: false,
          lock_override_reason: reason,
        })
        .eq('id', id)

      if (error) throw error

      setProposalData((prev) => ({
        ...prev,
        is_locked: false,
      }))

      setNotification({
        type: 'success',
        message: 'Proposal unlocked for editing',
      })
      setTimeout(() => setNotification(null), 3000)
    } catch (error: any) {
      setNotification({
        type: 'error',
        message: 'Error unlocking proposal: ' + error.message,
      })
      setTimeout(() => setNotification(null), 5000)
    }
  }

  return (
    <MobilePage className="max-w-6xl mx-auto pb-28 lg:pb-8">
      <MobilePageHeader
        sticky
        title="Edit Proposal"
        subtitle="Update proposal details"
        onBack={() => navigate('/proposals')}
        backLabel="Back to Proposals"
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCreateContract}
              className={`${mobile.successBtn} px-3 text-xs sm:text-sm`}
              title="Convert to Contract"
            >
              <FileTextIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Contract</span>
            </button>
            <button
              type="button"
              onClick={handleCreateJob}
              className={`${mobile.primaryBtn} px-3 text-xs sm:text-sm bg-purple-600 hover:bg-purple-700 active:bg-purple-800`}
              title="Convert to Job"
            >
              <PlusIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Job</span>
            </button>
          </div>
        }
      />

      {proposalData.is_locked && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="min-w-0">
              <p className="font-medium text-amber-800 text-sm">This proposal is locked</p>
              <p className="text-xs sm:text-sm text-amber-700">
                Approved proposals are locked to prevent accidental changes.
              </p>
            </div>
            <button
              type="button"
              onClick={handleUnlockProposal}
              className="min-h-[44px] px-4 py-2 bg-amber-600 text-white text-sm rounded-xl hover:bg-amber-700 transition-colors flex-shrink-0"
            >
              Unlock
            </button>
          </div>
        </div>
      )}

      {proposalData.prices_locked && !proposalData.is_locked && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
          <p className="text-sm text-blue-800">Prices are locked. Changes will be logged.</p>
        </div>
      )}

      <div className={mobile.surface}>
          <div className="sticky top-[3.25rem] z-10 flex overflow-x-auto scrollbar-none border-b border-gray-200 bg-white/95 backdrop-blur-md">
            <button
              type="button"
              onClick={() => setActiveTab('overview')}
              className={`px-4 sm:px-5 min-h-[48px] flex items-center whitespace-nowrap text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'overview'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Overview
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('items')}
              className={`px-4 sm:px-5 min-h-[48px] flex items-center whitespace-nowrap text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'items'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Line Items
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('billing')}
              className={`px-4 sm:px-5 min-h-[48px] flex items-center whitespace-nowrap text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'billing'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Billing
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('notes')}
              className={`px-4 sm:px-5 min-h-[48px] flex items-center whitespace-nowrap text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'notes'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Notes
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('request')}
              className={`px-4 sm:px-5 min-h-[48px] flex items-center whitespace-nowrap text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'request'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Request
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="p-4 sm:p-6">
              {consistencyIssues.length > 0 && (
                <div className="mb-4">
                  <ConsistencyWarnings
                    issues={consistencyIssues}
                    onNavigate={handleNavigateIssue}
                    currentTab={activeTab === 'items' ? 'services' : activeTab}
                  />
                </div>
              )}
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Proposal Title and Status - At the top */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <FileTextIcon className="h-5 w-5" />
                      Proposal Information
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Proposal Title *
                        </label>
                        <div className="flex items-start gap-2">
                          <input
                            type="text"
                            required
                            readOnly={!isTitleManual}
                            value={proposalData.title}
                            onChange={(e) =>
                              setProposalData({ ...proposalData, title: e.target.value })
                            }
                            className={`flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                              !isTitleManual ? 'bg-gray-100 text-gray-700 cursor-default' : ''
                            }`}
                            placeholder="Auto-generated from property address and service type"
                          />
                          {!isTitleManual ? (
                            <button
                              type="button"
                              onClick={() => setIsTitleManual(true)}
                              className="text-xs text-blue-600 hover:text-blue-800 underline shrink-0 pt-2"
                            >
                              Edit
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setIsTitleManual(false)}
                              className="text-xs text-blue-600 hover:text-blue-800 underline shrink-0 pt-2"
                            >
                              Use auto-generated
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Generated from the property address and service type unless you edit
                          manually.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                          <select
                            value={proposalData.status}
                            onChange={async (e) => {
                          const newStatus = e.target.value as
                            | 'draft'
                            | 'sent'
                            | 'viewed'
                            | 'approved'
                            | 'rejected'
                            | 'expired'

                          // Check if changing to approved and needs conversion
                          const isApproving = newStatus === 'approved' && originalStatus !== 'approved'
                          const hasLead = proposalData.lead_id && proposalData.lead_id !== ''
                          const hasCustomer = !!proposalData.customer_id

                          if (isApproving && hasLead && !hasCustomer) {
                            // Fetch lead if not loaded
                            if (!selectedLead && proposalData.lead_id) {
                              try {
                                const { data: leadData, error: leadError } = await supabase
                                  .from('leads')
                                  .select('*')
                                  .eq('id', proposalData.lead_id)
                                  .single()

                                if (!leadError && leadData) {
                                  setSelectedLead(leadData)
                                  populateConversionData(leadData)
                                  setShowConvertModal(true)
                                  return // Don't update status yet
                                } else {
                                  console.error('Error fetching lead for conversion:', leadError)
                                  alert('Error: Could not load lead information')
                                  return
                                }
                              } catch (error) {
                                console.error('Error fetching lead:', error)
                                alert('Error: Could not load lead information')
                                return
                              }
                            } else if (selectedLead) {
                              populateConversionData(selectedLead)
                              setShowConvertModal(true)
                              return // Don't update status yet
                            }
                          }

                          // Otherwise, update status normally
                          setProposalData({
                            ...proposalData,
                            status: newStatus,
                          })
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="draft">Draft</option>
                        <option value="sent">Sent</option>
                        <option value="viewed">Viewed</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="expired">Expired</option>
                        <option value="converted" disabled>Converted (Auto-set when contract/job created)</option>
                      </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Service Type *
                          </label>
                          <select
                            required
                            value={(proposalData as any).service_type}
                            onChange={(e) => {
                              const newServiceType = e.target.value
                              const defaultTemplate = getDefaultTemplateTypeForServiceType(newServiceType)
                              const defaultContract = getDefaultContractTypeForServiceType(newServiceType)
                              const nextContractType = newServiceType
                                ? defaultContract || proposalData.contract_type
                                : ('' as '' | ContractType)
                              const newDocData = newServiceType
                                ? buildDefaultDocumentData(newServiceType)
                                : ({} as DocumentData)
                              setProposalData({
                                ...proposalData,
                                service_type: newServiceType,
                                template_type: newServiceType ? defaultTemplate : '',
                                contract_type: nextContractType,
                                billing_frequency: normalizeBillingFrequency(
                                  nextContractType,
                                  proposalData.billing_frequency
                                ),
                                billing_method:
                                  newDocData.pricingMode === 'rate_card'
                                    ? 'actual_usage'
                                    : (proposalData as any).billing_method,
                                dsny_integration: isDsnyServiceType(newServiceType),
                                dsny_intent_message: isDsnyServiceType(newServiceType)
                                  ? 'Services will be scheduled based on DSNY pickup days for this property.'
                                  : '',
                                document_data: newDocData,
                              })
                              if (
                                newDocData.pricingMode === 'rate_card' &&
                                hasRateCardSeed(newServiceType)
                              ) {
                                setLineItems(mapRateCardToProposalLines(newServiceType))
                              }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">Select service type...</option>
                            {SERVICE_TYPE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            Sets job type and template type automatically
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div data-consistency-anchor="contract-type">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Job Type *
                          </label>
                          <select
                            value={proposalData.contract_type}
                            onChange={(e) => {
                              const contract_type = e.target.value as '' | ContractType
                              setProposalData({
                                ...proposalData,
                                contract_type,
                                billing_frequency: normalizeBillingFrequency(
                                  contract_type,
                                  proposalData.billing_frequency
                                ),
                              })
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">Select job type...</option>
                            <option value="one_time">One-Time Project</option>
                            <option value="as_needed">Ongoing (As Needed)</option>
                            <option value="recurring">Recurring Service</option>
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            {proposalData.contract_type === 'as_needed'
                              ? 'Open-ended agreement — jobs created when needed'
                              : proposalData.contract_type === 'recurring'
                                ? 'Recurring services will have interval scheduling options'
                                : proposalData.contract_type === 'one_time'
                                  ? 'One-time project with a single service date'
                                  : 'Filled from service type, or choose manually.'}
                          </p>
                        </div>

                        {/* Template Type is auto-set from Service Type (hidden to avoid a confusing duplicate dropdown). */}
                        <div data-consistency-anchor="template-type" className="hidden" aria-hidden="true" />
                      </div>

                      <ProposalValidityFields
                        validUntil={proposalData.valid_until}
                        onValidUntilChange={(value) =>
                          setProposalData({ ...proposalData, valid_until: value })
                        }
                        anchorDate={proposalCreatedAt || todayDateInputValue()}
                      />
                    </div>
                  </div>

                  {/* Two Column Layout: Customer/Property on left, Contact on right */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Left Column: Customer/Lead & Property/Project */}
                    <div className="space-y-6">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <UserIcon className="h-5 w-5" />
                          {entityType === 'customer' ? 'Customer & Property' : 'Lead & Project'}
                        </h3>

                        <div className="space-y-4">
                          <LeadCustomerSearchSelect
                            leads={leads}
                            customers={customers}
                            value={selectionFromIds(proposalData.lead_id, proposalData.customer_id)}
                            onChange={(selection) => {
                              if (!selection) {
                                setEntityType('customer')
                                setProposalData({
                                  ...proposalData,
                                  customer_id: '',
                                  lead_id: '',
                                  property_id: '',
                                })
                                setSelectedCustomerProperties([])
                                setSelectedLead(null)
                                return
                              }

                              setEntityType(selection.type)
                              if (selection.type === 'customer') {
                                setProposalData({
                                  ...proposalData,
                                  customer_id: selection.id,
                                  lead_id: '',
                                  property_id: '',
                                })
                                setSelectedCustomerProperties([])
                                setSelectedLead(null)
                              } else {
                                setProposalData({
                                  ...proposalData,
                                  lead_id: selection.id || null,
                                  customer_id: '',
                                  property_id: '',
                                })
                                setSelectedLead(null)
                                setSelectedCustomerProperties([])
                              }
                            }}
                            required
                            label="Send To"
                            placeholder="Type to search leads and customers..."
                          />

                          <PropertyProjectSearchSelect
                            mode={entityType === 'customer' ? 'property' : 'project'}
                            properties={selectedCustomerProperties}
                            projects={selectedLead?.projects || []}
                            value={proposalData.property_id}
                            onChange={(newPropertyId, option) => {
                              if (!option) {
                                setProposalData((prev) => ({
                                  ...prev,
                                  property_id: '',
                                  property_type: '',
                                }))
                                return
                              }

                              let newPropertyType = ''
                              if (entityType === 'customer') {
                                const property = option.raw as { property_type?: string }
                                newPropertyType = property.property_type || ''
                              } else {
                                const project = option.raw as { type?: string }
                                newPropertyType = project.type || ''
                              }

                              setProposalData((prev) => ({
                                ...prev,
                                property_id: newPropertyId,
                                property_type: newPropertyType,
                              }))
                            }}
                            onCreate={handleOpenCreateModal}
                            disabled={
                              entityType === 'customer'
                                ? !proposalData.customer_id
                                : !proposalData.lead_id
                            }
                            required
                            label={entityType === 'customer' ? 'Property' : 'Project'}
                            placeholder={
                              entityType === 'customer'
                                ? 'Type to search properties...'
                                : 'Type to search projects...'
                            }
                            id="proposal-edit-property-project-search"
                          />
                          {entityType === 'lead' && selectedLead?.projects?.length === 0 && (
                            <p className="text-xs text-gray-500 mt-1">
                              No projects found for this lead
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <BuildingIcon className="h-5 w-5" />
                          Additional Information
                        </h3>

                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Company Name
                            </label>
                            <input
                              type="text"
                              value={proposalData.company_name}
                              onChange={(e) =>
                                setProposalData({ ...proposalData, company_name: e.target.value })
                              }
                              readOnly={ownerContactReadOnly}
                              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                ownerContactReadOnly ? 'bg-gray-100 text-gray-700' : ''
                              }`}
                              placeholder="From linked customer or lead"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Property Type
                            </label>
                            <input
                              type="text"
                              value={proposalData.property_type}
                              onChange={(e) =>
                                setProposalData({ ...proposalData, property_type: e.target.value })
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Auto-populated from property/project"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right Column: Contact Information */}
                    <div className="space-y-6">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <MailIcon className="h-5 w-5" />
                          Contact Information
                        </h3>

                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Contact Name
                            </label>
                            <input
                              type="text"
                              value={proposalData.contact_name}
                              onChange={(e) =>
                                setProposalData({ ...proposalData, contact_name: e.target.value })
                              }
                              readOnly={ownerContactReadOnly}
                              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                ownerContactReadOnly ? 'bg-gray-100 text-gray-700' : ''
                              }`}
                              placeholder="From linked customer or lead"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Contact Email
                            </label>
                            <input
                              type="email"
                              value={proposalData.contact_email}
                              onChange={(e) =>
                                setProposalData({ ...proposalData, contact_email: e.target.value })
                              }
                              readOnly={ownerContactReadOnly}
                              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                ownerContactReadOnly ? 'bg-gray-100 text-gray-700' : ''
                              }`}
                              placeholder="From linked customer or lead"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Contact Phone
                            </label>
                            <input
                              type="tel"
                              value={proposalData.contact_phone}
                              onChange={(e) =>
                                setProposalData({ ...proposalData, contact_phone: e.target.value })
                              }
                              readOnly={ownerContactReadOnly}
                              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                ownerContactReadOnly ? 'bg-gray-100 text-gray-700' : ''
                              }`}
                              placeholder="From linked customer or lead"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Proposal Description - Below the two columns */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <FileTextIcon className="h-5 w-5" />
                      Proposal Description
                    </h3>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        value={proposalData.description}
                        onChange={(e) =>
                          setProposalData({ ...proposalData, description: e.target.value })
                        }
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter proposal description"
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'items' && (
                <div className="space-y-6">
                  <div className="bg-white p-6 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold text-gray-900">Line Items</h2>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={handleAutoDetectDSNYFrequencies}
                          className="flex items-center gap-2 px-4 py-2 border border-blue-500 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors text-sm"
                          title="Add Building Upkeep (with hallway) or Exterior Maintenance (DSNY only)"
                        >
                          {findExistingDsnyLineIndexes().length > 0 ? 'Edit DSNY' : 'Add DSNY'}
                        </button>
                        <button
                          type="button"
                          onClick={addLineItem}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <PlusIcon className="w-4 h-4" />
                          Add Item
                        </button>
                      </div>
                    </div>
                    {dsnyFreqNotice && (
                      <div className="mb-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                        {dsnyFreqNotice}
                      </div>
                    )}

                    <PricingPresentationFields
                      serviceType={(proposalData as any).service_type || ''}
                      pricingMode={proposalData.document_data?.pricingMode}
                      onPricingModeChange={handlePricingModeChange}
                      onSeedRateCard={handleSeedRateCard}
                      lineItemCount={lineItems.length}
                    />

                    <div className="space-y-4">
                      {lineItems.map((item, index) => (
                        <div
                          key={item.id}
                          className="p-4 border border-gray-200 rounded-lg bg-gray-50"
                        >
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Link to Service Item{' '}
                              <span className="text-gray-400 font-normal">(Optional)</span>
                            </label>
                            <ServiceItemSelect
                              value={item.service_item_id || ''}
                              onChange={(id) => handleServiceItemSelect(index, id)}
                              grouped={serviceItemsGrouped}
                              allowEmpty
                              emptyValueLabel="None - Custom Entry"
                              onCatalogRefresh={fetchServiceItems}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Selecting a service item will auto-fill description, unit type, and
                              price (you can still edit)
                            </p>
                          </div>

                          {/* Interval Configuration Row - Only for recurring contracts.
                              Mirrors the block in ProposalCreate so Edit and Create
                              expose the same recurrence controls. */}
                          {proposalData.contract_type === 'recurring' && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Schedule Type
                                </label>
                                <select
                                  value={item.recurrence_type}
                                  onChange={(e) =>
                                    handleLineItemChange(index, 'recurrence_type', e.target.value)
                                  }
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                  <option value="one_time">One-Time</option>
                                  <option value="daily">Daily</option>
                                  <option value="weekly">Weekly</option>
                                  <option value="bi_weekly">Bi-Weekly</option>
                                  <option value="monthly">Monthly</option>
                                  <option value="quarterly">Quarterly</option>
                                  <option value="annually">Annually</option>
                                  <option value="custom">Custom</option>
                                </select>
                              </div>

                              {item.recurrence_type !== 'one_time' && (
                                <>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                      Frequency
                                    </label>
                                    <div className="flex gap-2">
                                      <input
                                        type="number"
                                        min="1"
                                        max="31"
                                        value={item.recurrence_interval}
                                        onChange={(e) =>
                                          handleLineItemChange(
                                            index,
                                            'recurrence_interval',
                                            parseInt(e.target.value) || 1
                                          )
                                        }
                                        className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                      />
                                      <select
                                        value={item.interval_unit}
                                        onChange={(e) =>
                                          handleLineItemChange(
                                            index,
                                            'interval_unit',
                                            e.target.value
                                          )
                                        }
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                      >
                                        <option value="">Select unit...</option>
                                        <option value="days_per_week">days per week</option>
                                        <option value="days_per_month">days per month</option>
                                        <option value="every_x_weeks">every X weeks</option>
                                        <option value="every_x_months">every X months</option>
                                      </select>
                                    </div>
                                  </div>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                      Schedule Display
                                    </label>
                                    <div className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-600">
                                      {item.interval_unit === 'days_per_week' &&
                                        `${item.recurrence_interval} days per week`}
                                      {item.interval_unit === 'days_per_month' &&
                                        `${item.recurrence_interval} days per month`}
                                      {item.interval_unit === 'every_x_weeks' &&
                                        `Every ${item.recurrence_interval} week${
                                          item.recurrence_interval > 1 ? 's' : ''
                                        }`}
                                      {item.interval_unit === 'every_x_months' &&
                                        `Every ${item.recurrence_interval} month${
                                          item.recurrence_interval > 1 ? 's' : ''
                                        }`}
                                      {!item.interval_unit && 'Set frequency above'}
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          )}

                          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Description *
                              </label>
                              <input
                                type="text"
                                value={item.description}
                                onChange={(e) =>
                                  handleLineItemChange(index, 'description', e.target.value)
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Item description"
                              />
                              {/* Service-type chip, mirrors ProposalView for parity */}
                              {item.service_type && (
                                <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-700">
                                    {String(item.service_type).replace(/_/g, ' ')}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2 relative pr-24">
                                Quantity
                                <div className="absolute right-0 -top-1 flex items-start gap-1.5">
                                  <input
                                    type="checkbox"
                                    id={`variable-${item.id}`}
                                    checked={item.is_variable}
                                    onChange={(e) =>
                                      handleLineItemChange(index, 'is_variable', e.target.checked)
                                    }
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5"
                                  />
                                  <label
                                    htmlFor={`variable-${item.id}`}
                                    className="text-xs text-gray-600 font-normal leading-tight"
                                  >
                                    Variable
                                    <br />
                                    Quantity
                                  </label>
                                </div>
                              </label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.quantity}
                                onChange={(e) =>
                                  handleLineItemChange(
                                    index,
                                    'quantity',
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                disabled={item.is_variable}
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                  item.is_variable
                                    ? 'border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed'
                                    : 'border-gray-300'
                                }`}
                                placeholder={item.is_variable ? 'Variable' : '0'}
                              />
                              {item.is_variable && (
                                <p className="mt-1 text-xs text-gray-500">Based on actual usage</p>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Unit Type
                              </label>
                              <LineItemUnitTypeSelect
                                value={item.unit_type}
                                contractType={(proposalData.contract_type || 'one_time') as ContractType}
                                onChange={(value) =>
                                  handleLineItemChange(index, 'unit_type', value)
                                }
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Unit Price
                              </label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.unit_price}
                                onChange={(e) =>
                                  handleLineItemChange(
                                    index,
                                    'unit_price',
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Total
                                </label>
                                <div
                                  className={`px-3 py-2 rounded-lg border text-sm font-medium ${
                                    item.is_variable
                                      ? 'bg-orange-50 border-orange-300 text-orange-700'
                                      : 'bg-white border-gray-300'
                                  }`}
                                >
                                  {(() => {
                                    // For variable recurring items, show unit_price with billing interval
                                    if (item.is_variable && item.recurrence_type && item.recurrence_type !== 'one_time') {
                                      const frequencyLabels: Record<string, string> = {
                                        monthly: '/month',
                                        quarterly: '/quarter',
                                        annually: '/year',
                                        one_time: '',
                                      }
                                      const interval = frequencyLabels[proposalData.billing_frequency] || ''
                                      return `$${item.unit_price.toFixed(2)}${interval}`
                                    }
                                    // For variable one-time items, just show "Variable"
                                    if (item.is_variable) {
                                      return 'Variable'
                                    }
                                    // For fixed items, show total_price
                                    return `$${item.total_price.toFixed(2)}`
                                  })()}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeLineItem(index)}
                                className="p-2 text-red-600 hover:text-red-800 transition-colors mt-6"
                                title="Remove item"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {lineItems.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <p className="text-sm">No line items added yet.</p>
                        <p className="text-xs mt-1">Click "Add Item" to get started.</p>
                      </div>
                    )}

                    {/* Line Items Total */}
                    {lineItems.length > 0 && (
                      <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                        <div className="flex justify-between items-center">
                          <span className="text-lg font-semibold text-gray-900">
                            {(() => {
                              // Check if there are variable quantity recurring items
                              const hasVariableRecurring = lineItems.some(
                                (item) => item.is_variable && item.recurrence_type && item.recurrence_type !== 'one_time'
                              )

                              if (hasVariableRecurring) {
                                // Show billing period based on billing_frequency
                                const frequencyLabels: Record<string, string> = {
                                  monthly: 'Monthly Subtotal',
                                  quarterly: 'Quarterly Subtotal',
                                  annually: 'Annual Subtotal',
                                  one_time: 'Line Items Subtotal',
                                }
                                return frequencyLabels[proposalData.billing_frequency] || 'Line Items Subtotal'
                              }
                              return 'Line Items Subtotal'
                            })()}:
                          </span>
                          <span className="text-2xl font-bold text-blue-600">
                            ${(() => {
                              // Use same calculation as in useEffect - for variable recurring items, use unit_price
                              return lineItems.reduce((sum, item) => {
                                if (item.is_variable && item.recurrence_type && item.recurrence_type !== 'one_time') {
                                  return sum + item.unit_price
                                }
                                return sum + item.total_price
                              }, 0).toFixed(2)
                            })()}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Document generation settings */}
                  {proposalData.service_type && (
                    <div className="bg-white border border-gray-200 rounded-lg p-4 mt-6">
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Scope of Work (intro shown under Compensation)
                        </label>
                        <textarea
                          rows={3}
                          value={proposalData.scope_of_work || ''}
                          onChange={(e) =>
                            setProposalData({ ...proposalData, scope_of_work: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Optional narrative describing the work to be performed"
                        />
                      </div>
                      <DocumentDataEditor
                        serviceType={proposalData.service_type}
                        phase="proposal"
                        value={proposalData.document_data || {}}
                        onChange={(dd) => setProposalData({ ...proposalData, document_data: dd })}
                      />
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'billing' && (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-800">
                      <strong>Note:</strong> Subtotal, Tax Amount, and Total are automatically
                      calculated from the Line Items you added in the previous tab.
                    </p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Billing Configuration</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div data-consistency-anchor="billing-frequency">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Billing Frequency *
                        </label>
                        <BillingFrequencySelect
                          value={proposalData.billing_frequency}
                          contractType={
                            proposalData.contract_type === 'recurring' ? 'recurring' : 'one_time'
                          }
                          onChange={(value) => {
                            const billing_method = normalizeBillingMethod(
                              value,
                              (proposalData as any).billing_method
                            )
                            if (value === 'progress' && billingMilestones.length === 0) {
                              setBillingMilestones([createEmptyMilestone(0)])
                            }
                            setProposalData({
                              ...proposalData,
                              billing_frequency: value,
                              billing_method,
                            } as typeof proposalData)
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <BillingDayOfMonthSelect
                        billingFrequency={proposalData.billing_frequency}
                        value={(proposalData as any).billing_day_of_month ?? 1}
                        onChange={(day) =>
                          setProposalData({
                            ...proposalData,
                            billing_day_of_month: day,
                          } as typeof proposalData)
                        }
                      />

                      {proposalData.contract_type === 'recurring' &&
                        proposalData.billing_frequency &&
                        proposalData.billing_frequency !== 'one_time' &&
                        proposalData.billing_frequency !== 'after_each_service' &&
                        proposalData.billing_frequency !== 'progress' && (
                          <div data-consistency-anchor="billing-method">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Billing Method
                            </label>
                            <BillingMethodSelect
                              value={(proposalData as any).billing_method || ''}
                              billingFrequency={proposalData.billing_frequency}
                              onChange={(value) =>
                                setProposalData({
                                  ...proposalData,
                                  billing_method: value,
                                } as typeof proposalData)
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        )}

                      {proposalData.billing_frequency === 'progress' && (
                        <div className="md:col-span-2" data-consistency-anchor="billing-method">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Billing Method
                          </label>
                          <BillingMethodSelect
                            value="progress"
                            billingFrequency="progress"
                            onChange={() => {}}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                          />
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Late Fee % <span className="text-gray-400">(Optional)</span>
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={proposalData.late_fee_percentage || ''}
                          onChange={(e) =>
                            setProposalData({
                              ...proposalData,
                              late_fee_percentage: parseFloat(e.target.value) || 0,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    {proposalData.billing_frequency === 'progress' && (
                      <MilestoneScheduleEditor
                        className="mt-4 p-4 bg-white border border-gray-200 rounded-lg"
                        milestones={billingMilestones}
                        totalAmount={proposalData.total_amount}
                        onChange={setBillingMilestones}
                      />
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Subtotal
                      </label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-lg font-semibold text-gray-900">
                        ${proposalData.subtotal.toFixed(2)}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Auto-calculated from line items</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sales Tax Status
                      </label>
                      <select
                        value={proposalData.sales_tax_status}
                        onChange={(e) => {
                          const status = e.target.value
                          setProposalData({
                            ...proposalData,
                            sales_tax_status: status,
                          })
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Use Owner Default</option>
                        <option value="taxable">Taxable</option>
                        <option value="non-tax">Non-Tax</option>
                        <option value="exempt">Exempt</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Defaults to owner's sales tax status but can be overridden per proposal
                      </p>
                    </div>

                    {proposalData.sales_tax_status === 'taxable' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Tax Rate (%)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={proposalData.tax_rate}
                          onChange={(e) => {
                            const rate = parseFloat(e.target.value) || 0
                            setProposalData({
                              ...proposalData,
                              tax_rate: rate,
                              tax_amount: (proposalData.subtotal * rate) / 100,
                              total_amount:
                                proposalData.subtotal + (proposalData.subtotal * rate) / 100,
                            })
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="0.00"
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tax Amount
                      </label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-lg font-semibold text-gray-900">
                        ${proposalData.tax_amount.toFixed(2)}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Auto-calculated: Subtotal × Tax Rate (if taxable)
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {(() => {
                          // Check if there are variable quantity recurring items
                          const hasVariableRecurring = lineItems.some(
                            (item) => item.is_variable && item.recurrence_type && item.recurrence_type !== 'one_time'
                          )

                          if (hasVariableRecurring) {
                            // Show billing period based on billing_frequency
                            const frequencyLabels: Record<string, string> = {
                              monthly: 'Monthly Total',
                              quarterly: 'Quarterly Total',
                              annually: 'Annual Total',
                              one_time: 'Total Amount',
                            }
                            return frequencyLabels[proposalData.billing_frequency] || 'Total Amount'
                          }
                          return 'Total Amount'
                        })()}
                      </label>
                      <div className="px-3 py-2 bg-blue-50 border border-blue-300 rounded-lg text-xl font-bold text-blue-600">
                        ${proposalData.total_amount.toFixed(2)}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Auto-calculated: Subtotal + Tax Amount
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Payment Terms (Days)
                      </label>
                      <input
                        type="number"
                        value={proposalData.payment_terms}
                        onChange={(e) =>
                          setProposalData({
                            ...proposalData,
                            payment_terms: parseInt(e.target.value) || 30,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Terms & Conditions
                    </label>
                    <textarea
                      value={proposalData.terms_and_conditions}
                      onChange={(e) =>
                        setProposalData({ ...proposalData, terms_and_conditions: e.target.value })
                      }
                      rows={6}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              )}

              {activeTab === 'notes' && (
                <NotesAndActivityPanel
                  notes={notes.map((n) => ({ note: n.note, timestamp: n.timestamp }))}
                  activities={[]}
                  notesTitle="Proposal Notes"
                  newNote={newNote}
                  onNewNoteChange={setNewNote}
                  onAddNote={addNote}
                  savingNote={savingNote}
                />
              )}

              {/* Request Tab */}
              {activeTab === 'request' && (
                <div className="space-y-6">
                  {loadingRequest ? (
                    <div className="text-center py-8">
                      <p className="text-gray-600">Loading request information...</p>
                    </div>
                  ) : proposalRequest ? (
                    <ProposalRequestTabPanel
                      request={proposalRequest}
                      contact={proposalRequestContact}
                      serviceCategories={serviceCategories}
                      viewRequestHref={
                        id
                          ? `/proposal-requests/view/${id}?returnTo=${encodeURIComponent(`/proposals/edit/${id}`)}`
                          : undefined
                      }
                    />
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-600">No proposal request found for this proposal.</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Submit Buttons */}
            <div className="native-action-bar flex items-center justify-between gap-3 max-w-6xl mx-auto lg:mt-2 lg:rounded-xl lg:border lg:border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/proposals')}
                className={mobile.secondaryBtn}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className={mobile.primaryBtn}
              >
                <span className="sm:hidden">{loading ? 'Saving...' : 'Save'}</span>
                <span className="hidden sm:inline">{loading ? 'Updating...' : 'Update Proposal'}</span>
              </button>
            </div>
          </form>
        </div>

      {/* Convert Lead to Customer Modal */}
      {showConvertModal && selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900">Convert Lead to Customer</h3>
              <button
                onClick={() => {
                  setShowConvertModal(false)
                  // Reset status to original if cancelled
                  setProposalData({
                    ...proposalData,
                    status: originalStatus as 'draft' | 'sent' | 'viewed' | 'approved' | 'rejected' | 'expired' | 'converted',
                  })
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>This proposal is about to be approved.</strong> To complete the approval,
                please confirm the lead conversion:
              </p>
              <ul className="list-disc list-inside text-sm text-blue-700 mt-2">
                <li>Create a new customer record from this lead</li>
                <li>Create property records from lead projects</li>
                <li>Mark the lead as "Won"</li>
                <li>Link the proposal to the new customer</li>
              </ul>
            </div>

            <div className="space-y-6">
              {/* Company Information */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Company Name</label>
                    <input
                      type="text"
                      value={conversionData.company_name}
                      onChange={(e) => handleConversionFormChange('company_name', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Company Phone</label>
                    <input
                      type="tel"
                      value={conversionData.company_phone}
                      onChange={(e) => handleConversionFormChange('company_phone', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Company Email</label>
                    <input
                      type="email"
                      value={conversionData.company_email}
                      onChange={(e) => handleConversionFormChange('company_email', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Company Website</label>
                    <input
                      type="text"
                      value={conversionData.company_website}
                      onChange={(e) => handleConversionFormChange('company_website', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Company Address</label>
                    <input
                      type="text"
                      value={conversionData.company_address}
                      onChange={(e) => handleConversionFormChange('company_address', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Company Address Line 2</label>
                    <input
                      type="text"
                      value={conversionData.company_address_line_2}
                      onChange={(e) => handleConversionFormChange('company_address_line_2', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">City</label>
                    <input
                      type="text"
                      value={conversionData.company_city}
                      onChange={(e) => handleConversionFormChange('company_city', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">State</label>
                    <input
                      type="text"
                      value={conversionData.company_state}
                      onChange={(e) => handleConversionFormChange('company_state', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Zip Code</label>
                    <input
                      type="text"
                      value={conversionData.company_zip}
                      onChange={(e) => handleConversionFormChange('company_zip', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Primary Contact Information */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Primary Contact Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Contact Name</label>
                    <input
                      type="text"
                      value={conversionData.contact_name}
                      onChange={(e) => handleConversionFormChange('contact_name', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      value={conversionData.email}
                      onChange={(e) => handleConversionFormChange('email', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                    <input
                      type="tel"
                      value={conversionData.phone}
                      onChange={(e) => handleConversionFormChange('phone', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Cell</label>
                    <input
                      type="tel"
                      value={conversionData.cell}
                      onChange={(e) => handleConversionFormChange('cell', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* All Contacts */}
              {conversionData.contacts.length > 0 && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">All Contacts</h4>
                  <div className="space-y-3">
                    {conversionData.contacts.map((contact, index) => (
                      <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600">Name</label>
                            <p className="text-sm text-gray-900">{contact.name || 'N/A'}</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600">Phone</label>
                            <p className="text-sm text-gray-900">{contact.phone || 'N/A'}</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600">Cell</label>
                            <p className="text-sm text-gray-900">{contact.cell || 'N/A'}</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600">Email</label>
                            <p className="text-sm text-gray-900">{contact.email || 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Properties */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-semibold text-gray-900">Properties</h4>
                  <button
                    type="button"
                    onClick={addProperty}
                    className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    <PlusIcon className="w-4 h-4" />
                    Add Property
                  </button>
                </div>

                {conversionData.properties.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">
                    No properties. Click "Add Property" to add one.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {conversionData.properties.map((property, index) => (
                      <div key={index} className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex justify-between items-center mb-3">
                          <h5 className="font-medium text-gray-900">Property {index + 1}</h5>
                          <button
                            type="button"
                            onClick={() => removeProperty(index)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Property Name
                            </label>
                            <input
                              type="text"
                              value={property.name}
                              onChange={(e) => handlePropertyChange(index, 'name', e.target.value)}
                              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Address
                            </label>
                            <input
                              type="text"
                              value={property.address}
                              onChange={(e) =>
                                handlePropertyChange(index, 'address', e.target.value)
                              }
                              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700">
                              Address Line 2
                            </label>
                            <input
                              type="text"
                              value={property.address_line_2}
                              onChange={(e) =>
                                handlePropertyChange(index, 'address_line_2', e.target.value)
                              }
                              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">City</label>
                            <input
                              type="text"
                              value={property.city}
                              onChange={(e) => handlePropertyChange(index, 'city', e.target.value)}
                              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">State</label>
                            <input
                              type="text"
                              value={property.state}
                              onChange={(e) => handlePropertyChange(index, 'state', e.target.value)}
                              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Zip</label>
                            <input
                              type="text"
                              value={property.zip}
                              onChange={(e) => handlePropertyChange(index, 'zip', e.target.value)}
                              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Property Type
                            </label>
                            <input
                              type="text"
                              value={property.type}
                              onChange={(e) => handlePropertyChange(index, 'type', e.target.value)}
                              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Unit Count
                            </label>
                            <input
                              type="text"
                              value={property.unit_count}
                              onChange={(e) =>
                                handlePropertyChange(index, 'unit_count', e.target.value)
                              }
                              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Work Type
                            </label>
                            <select
                              value={property.work_type}
                              onChange={(e) => handlePropertyChange(index, 'work_type', e.target.value)}
                              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                            >
                              <option value="">Select Work Type</option>
                              {serviceCategories.map((category) => (
                                <option key={category.id} value={category.id}>
                                  {category.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700">Notes</label>
                            <textarea
                              value={property.notes}
                              onChange={(e) => handlePropertyChange(index, 'notes', e.target.value)}
                              rows={2}
                              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowConvertModal(false)
                  // Reset status to original if cancelled
                  setProposalData({
                    ...proposalData,
                    status: originalStatus as 'draft' | 'sent' | 'viewed' | 'approved' | 'rejected' | 'expired' | 'converted',
                  })
                }}
                disabled={converting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmConversion}
                disabled={converting}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {converting ? 'Converting...' : 'Confirm & Approve Proposal'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Property/Project Modal */}
      {showCreatePropertyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  Create New {entityType === 'customer' ? 'Property' : 'Project'}
                </h2>
                <button
                  onClick={() => {
                    setShowCreatePropertyModal(false)
                    setNewPropertyData({
                      name: '',
                      address: '',
                      city: '',
                      state: '',
                      zip: '',
                      property_type: '',
                    })
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                {entityType === 'customer' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Property Name *
                    </label>
                    <input
                      type="text"
                      value={newPropertyData.name}
                      onChange={(e) =>
                        setNewPropertyData({ ...newPropertyData, name: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter property name"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address {entityType === 'lead' ? '*' : ''}
                  </label>
                  <AddressAutocomplete
                    value={newPropertyData.address}
                    onChange={(address) =>
                      setNewPropertyData({ ...newPropertyData, address })
                    }
                    onAddressSelect={(addressData) => {
                      setNewPropertyData({
                        ...newPropertyData,
                        address: addressData.address,
                        city: addressData.city,
                        state: addressData.state,
                        zip: addressData.zip,
                      })
                    }}
                    placeholder="Start typing address..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                    <input
                      type="text"
                      value={newPropertyData.city}
                      onChange={(e) =>
                        setNewPropertyData({ ...newPropertyData, city: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="City"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                    <input
                      type="text"
                      value={newPropertyData.state}
                      onChange={(e) =>
                        setNewPropertyData({ ...newPropertyData, state: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="State"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code</label>
                  <input
                    type="text"
                    value={newPropertyData.zip}
                    onChange={(e) =>
                      setNewPropertyData({ ...newPropertyData, zip: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="ZIP"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setShowCreatePropertyModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (entityType === 'customer') {
                      handleCreateProperty()
                    } else {
                      handleCreateProject()
                    }
                  }}
                  disabled={creatingProperty}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creatingProperty ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Job Modal - shown after successful conversion */}
      {showCreateJobModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Conversion Successful!</h3>
              <p className="text-sm text-gray-600 mb-6">
                Lead has been converted to a customer and the proposal has been approved.
              </p>
              <p className="text-sm text-gray-700 font-medium mb-6">
                What would you like to do next?
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => {
                    setShowCreateJobModal(false)
                    navigate(`/jobs/create?proposalId=${id}`)
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                >
                  Create Job
                </button>
                <button
                  onClick={() => {
                    setShowCreateJobModal(false)
                    navigate(`/contracts/create?proposalId=${id}`)
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
                >
                  Create Contract
                </button>
                <button
                  onClick={() => setShowCreateJobModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Not Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification Popup Modal */}
      {notification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            className={`bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl ${
              notification.type === 'success'
                ? 'border-2 border-green-500'
                : 'border-2 border-red-500'
            }`}
          >
            <div className="flex items-center gap-4">
              <div
                className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                  notification.type === 'success'
                    ? 'bg-green-100'
                    : 'bg-red-100'
                }`}
              >
                {notification.type === 'success' ? (
                  <svg
                    className="w-6 h-6 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-6 h-6 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                )}
              </div>
              <div className="flex-1">
                <p
                  className={`text-lg font-semibold ${
                    notification.type === 'success' ? 'text-green-800' : 'text-red-800'
                  }`}
                >
                  {notification.message}
                </p>
              </div>
              <button
                onClick={() => setNotification(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DSNY package scheduling modal */}
      {showDsnyModal && (() => {
        const existingDsnyIdxs = findExistingDsnyLineIndexes()
        const isReplacing = existingDsnyIdxs.length > 0
        const isExterior = dsnyPackageKind === 'exterior_maintenance'
        const previewName = isExterior ? 'Exterior Maintenance' : 'Building Upkeep'
        const effectiveHallway = isExterior ? 0 : hallwayVisitsPerWeek
        return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {isReplacing ? 'Edit DSNY package' : 'Add DSNY package'}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Choose Building Upkeep (pickups + optional hallway) or Exterior Maintenance
                (pickups only). The proposal gets one line item; the contract expands the schedule.
              </p>
            </div>

            <div className="px-6 py-4 space-y-4">
              {isReplacing && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  This will replace your existing DSNY package line on the proposal.
                </div>
              )}

              <div>
                <p className="block text-sm font-medium text-gray-700 mb-2">Package</p>
                <div className="flex flex-col gap-2 text-sm">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="radio"
                      name="dsny_package_kind_edit"
                      checked={dsnyPackageKind === 'building_upkeep'}
                      onChange={() => {
                        setDsnyPackageKind('building_upkeep')
                        if (hallwayVisitsPerWeek === 0) setHallwayVisitsPerWeek(1)
                      }}
                    />
                    Building Upkeep (DSNY + hallway)
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="radio"
                      name="dsny_package_kind_edit"
                      checked={dsnyPackageKind === 'exterior_maintenance'}
                      onChange={() => {
                        setDsnyPackageKind('exterior_maintenance')
                        setHallwayVisitsPerWeek(0)
                      }}
                    />
                    Exterior Maintenance (DSNY only, no hallway)
                  </label>
                </div>
              </div>

              {dsnyModalLoading && (
                <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                  Checking DSNY for this address...
                </div>
              )}
              {!dsnyModalLoading && dsnyModalDetected && (
                <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900">
                  Detected at this address:{' '}
                  {[
                    dsnyModalDetected.garbage > 0
                      ? `garbage ${dsnyModalDetected.garbage}×/wk`
                      : null,
                    dsnyModalDetected.recycling > 0
                      ? `recycling ${dsnyModalDetected.recycling}×/wk`
                      : null,
                    dsnyModalDetected.organics > 0
                      ? `organics ${dsnyModalDetected.organics}×/wk`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(', ') || 'no pickups'}
                  . That&apos;s <strong>{dsnyModalDetected.total}</strong> unique pickup day
                  {dsnyModalDetected.total === 1 ? '' : 's'}/week.
                </div>
              )}
              {!dsnyModalLoading && dsnyModalError && (
                <div className="rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-yellow-900">
                  {dsnyModalError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  DSNY pickup visits per week
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    max={7}
                    value={dsnyVisitsPerWeek}
                    onChange={(e) => setDsnyVisitsPerWeek(parseInt(e.target.value, 10) || 0)}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <span className="text-sm text-gray-500">times / week</span>
                </div>
              </div>

              {!isExterior && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hallway cleaning visits per week
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={0}
                      max={7}
                      value={hallwayVisitsPerWeek}
                      onChange={(e) => setHallwayVisitsPerWeek(parseInt(e.target.value, 10) || 0)}
                      className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <span className="text-sm text-gray-500">times / week</span>
                  </div>
                </div>
              )}

              <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                <strong>Preview:</strong> {previewName} —{' '}
                {[
                  dsnyVisitsPerWeek > 0 ? `DSNY ${dsnyVisitsPerWeek}×/wk` : null,
                  effectiveHallway > 0 ? `hallway ${effectiveHallway}×/wk` : null,
                ]
                  .filter(Boolean)
                  .join(' + ') || 'nothing selected'}
                {dsnyVisitsPerWeek + effectiveHallway > 0 && (
                  <span>
                    {' '}
                    (<strong>{dsnyVisitsPerWeek + effectiveHallway}</strong> total visits/week)
                  </span>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowDsnyModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDsnyAdd}
                disabled={dsnyVisitsPerWeek + effectiveHallway === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isReplacing ? 'Save changes' : 'Add to proposal'}
              </button>
            </div>
          </div>
        </div>
        )
      })()}
    </MobilePage>
  )
}